import { AfterViewInit, Component, DoCheck, ElementRef, HostListener, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatDrawer } from '@angular/material/sidenav';
import { TooltipPosition } from '@angular/material/tooltip';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../../../../environments/environment';
import { AppComponent } from '../../../app.component';
import { HasUnsavedChanges } from '../../../route-guards/unsaved-changes.route-guard';
import { CurrentUserService } from '../../../security/current-user.service';
import { AccessibilityHelper } from '../../../shared/accessibility.helper';
import { ScriptableBaseComponent } from '../../../shared/components/scriptable-base/scriptable-base.component';
import { AlertDialogComponent, AlertDialogModel } from '../../../shared/dialogs/alert/alert-dialog.component';
import { ConfigureRolePermissionsDialog } from '../../../shared/dialogs/configure-role-permissions/configure-role-permissions.dialog';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '../../../shared/dialogs/confirmation/confirmation-dialog.component';
import { WorkflowViewingMode } from '../../../shared/enums/workflow-viewing-mode.enum';
import { WorkflowEnum } from '../../../shared/enums/workflow.enum';
import { ICloneAndCopy } from '../../../shared/interfaces/clone-and-copy';
import { IHasIdAndName } from '../../../shared/interfaces/has-id-and-name.interface';
import { IHasWorkflowPermissions } from '../../../shared/interfaces/has-workflow-permissions.interface';
import { INameToPrettyNameMap } from '../../../shared/models/component-scripting/additional-element-info.model';
import { WorkflowErrorMessages } from '../../../shared/models/error-messages/workflow-error-messages';
import { Role } from '../../../shared/models/role.model';
import { DataCollection } from '../../../shared/models/site-content/data-collection.model';
import { FormInstancesAndFoldersUsingWorkflows } from '../../../shared/models/site-content/form-instance-using-workflowstate.model';
import { Workflow } from '../../../shared/models/site-content/workflow.model';
import { WorkflowPermissionBase } from '../../../shared/models/workflow-permission-base.model';
import { WorkflowStatePermission } from '../../../shared/models/workflow-state-permission.model';
import { WorkflowState } from '../../../shared/models/workflow-state.model';
import { WorkflowTransitionPermission } from '../../../shared/models/workflow-transition-permission.model';
import { WorkflowTransition } from '../../../shared/models/workflow-transition.model';
import { CommunicationService } from '../../../shared/services/communication.service';
import { CurrentSiteService } from '../../../shared/services/current-site.service';
import { DataCollectionService } from '../../../shared/services/data-collection.service';
import { FormInstanceService } from '../../../shared/services/form-instance.service';
import { ProgressIndicatorService } from '../../../shared/services/progress-indicator.service';
import { WorkflowService } from '../../../shared/services/workflow.service';

// Pull in jQuery with the following line.
declare let $: any;


// Define data structures used for the permissions grid.

// Define a type-safe hash of WorkflowState instances by Id.
interface IWorkflowStateIdToWorkflowState {
    [workflowStateId: number]: WorkflowState;
}

// Define some constants used in this class.
// Note:  with a bit more work we should be able to remove the following constants.
const FORWARD_TRANSITION_START_STATE_DIV_CSS_CLASS: string = 'forward-transition-start-state-div';
const FORWARD_TRANSITION_END_STATE_DIV_CSS_CLASS: string = 'forward-transition-end-state-div';
const FORWARD_TRANSITION_INTERMEDIATE_STATE_DIV_CSS_CLASS: string = 'forward-transition-intermediate-state-div';
const FORWARD_TRANSITION_NA_STATE_DIV_CSS_CLASS: string = 'transition-not-applicable-state-div';
const FORWARD_TRANSITION_INVISIBLE_CONNECTOR_CSS_CLASS: string = 'invisible-forward-transition-div';

// Define a class used when dragging a workflow
// state connector to create a new transition.
class WorkflowStateConnector {
    public workflowState: WorkflowState = null;

    public constructor(workflowStateParam: WorkflowState) {
        this.workflowState = workflowStateParam;

        return;
    }
}

interface IWorkflowStateClientIdToWorkflowStateConnector {
    [workflowStateClientId: number]: WorkflowStateConnector;
}

// Define an internally used interface for undoing
// drawer operations when the user clicks 'Cancel'.
interface IDrawerCancelledOperator {
    drawerCancelled(): void;
}

// Define internally used classes that
// implement the above interface.
class WorkflowObjectEditCancelled implements IDrawerCancelledOperator {
    // Instance data.
    private workflowObject: ICloneAndCopy = null;
    private workflowObjectCopy: ICloneAndCopy = null;

    // Constructor.
    public constructor(workflowObjectParam: ICloneAndCopy) {
        this.workflowObject = workflowObjectParam;

        this.workflowObjectCopy = this.workflowObject.clone();

        return;
    }

    // Implement IDrawerCancelledOperator method(s).
    public drawerCancelled(): void {
        // Copy the workflow state
        // back to its original state.
        this.workflowObject.copy(this.workflowObjectCopy);
        return;
    }
}

// Define a messages enum.
enum WorkflowComponentMessage {
    SAVING = 'Saving ...',
    COMPLETED = 'Completed',

    NO_WORKFLOW_STATE_DIALOGUE_TITLE = 'Zero Workflow State Warning',
    NO_WORKFLOW_STATE_DIALOGUE_MESSAGE = 'A workflow requires at least one workflow state, so an initial state will be created'
}

// Define a div name enum.
enum HtmlElementId {
    BUILDER_TOOLS_DIV = 'builder-tools-div',
    ROLES_DIV = 'roles-div',
    TRASH_DIV = 'trash-div'
}

// Define an internally used enum of CSS class name.
enum WorkflowCssClassNameEnum {
    SHOWN_BUILDER_TOOLS_DIV = 'shown-builder-tools-div',
    HIDE_BUILDER_TOOLS_DIV = 'hide-builder-tools-div',
    SHOW_BUILDER_TOOLS_DIV = 'show-builder-tools-div',

    SHOWN_ROLES_DIV = 'shown-roles-div',
    HIDE_ROLES_DIV = 'hide-roles-div',
    SHOW_ROLES_DIV = 'show-roles-div',

    SHOW_TRASH_DIV = 'show-trash-div',
    HIDE_TRASH_DIV = 'hide-trash-div'
}

// Define a workflow state type enum and class.
enum WorkflowStateType {
    NOT_STARTED = 'Not Started',
    IN_PROGRESS = 'In Progress',
    COMPLETED = 'Completed',

    NOT_STARTED_COLOR = 'blue',
    IN_PROGRESS_COLOR = 'yellow',
    COMPLETED_COLOR = 'green',

    NOT_STARTED_CSS_CLASS = 'not-started-workflow-state-div',
    IN_PROGRESS_CSS_CLASS = 'in-progress-workflow-state-div',
    COMPLETED_CSS_CLASS = 'completed-workflow-state-div'
}

class WorkflowStateTypeInfo {
    public typeId: number; // This is also an index into the generated workflow state 
    // type array returned by method this.WorkflowStateTypes.

    public typeName: string;
    public typeColorName: string;
    public colorCssClass: string;
}

enum PermissionsSummaryEnum {
    None = 'none',
    WORKFLOW_STATES = 'states',
    WORKFLOW_TRANSITIONS = 'transitions'
}

// Define an internally used class of <div/> Ids.
class WorkflowDivIds {
    public readonly strBuilderToolsDivId: string;
    public readonly strRolesDivId: string;
    public readonly strTrashDivId: string;

    public constructor(builderToolsDivId: string, rolesDivId: string, trashDivId: string) {
        this.strBuilderToolsDivId = builderToolsDivId;
        this.strRolesDivId = rolesDivId;
        this.strTrashDivId = trashDivId;
    }
}

@Component({
    selector: 'app-workflow',
    templateUrl: './workflow.component.html',
    styleUrls: ['./workflow.component.scss'],
    standalone: false
})
export class WorkflowComponent extends ScriptableBaseComponent implements OnInit, AfterViewInit, DoCheck, HasUnsavedChanges {
    // Properties.
    private site: DataCollection = null;
    private workflow: Workflow = null;
    private hshWorkflowStatesById: IWorkflowStateIdToWorkflowState = null;
    private hshWorkflowStatesByClientId: IWorkflowStateIdToWorkflowState = null;
    private forwardWorkflowTransitions: WorkflowTransition[] = null;
    private backwardWorkflowTransitions: WorkflowTransition[] = null;

    private hasWorkflowPermissions: IHasWorkflowPermissions = null;

    private hasIdAndName: IHasIdAndName = null;
    private idAndNameObjectType: string = null;
    private bHasWorkflowStateType: boolean = false;
    private bHasWorkflowTransitionType: boolean = false;
    private nameFieldTip: string = null;

    private workflowStateConnectorBeingDragged: WorkflowState = null;
    private iDraggedWorkflowStateConnectorIndex: number = -1;
    private iExistingWorkflowStateBeingDraggedIndex: number = -1;
    private workflowTransitionBeingDragged: WorkflowTransition = null;

    private isDirty: boolean = false;

    @ViewChild('workflowStatesDiv') workflowStatesDiv: ElementRef;
    @ViewChild('permissionsDrawer') multiPurposeDrawer: MatDrawer;
    private drawerCancelledOperator: IDrawerCancelledOperator = null;

    private viewingMode: WorkflowViewingMode = WorkflowViewingMode.PermissionsMode;

    private divIds: WorkflowDivIds =
        new WorkflowDivIds(HtmlElementId.BUILDER_TOOLS_DIV, HtmlElementId.ROLES_DIV, HtmlElementId.TRASH_DIV);

    @ViewChild('builderToolsDiv', { read: ElementRef }) builderToolsDiv: Element;
    @ViewChild('rolesDiv') rolesDiv: ElementRef;

    // Properties for context menus.
    public buildContextMenuPosition = { x: '0px', y: '0px' };
    @ViewChild('buildContextMenuTrigger', { read: MatMenuTrigger }) buildContextMenuTrigger: MatMenuTrigger;
    public roleContextMenuPosition = { x: '0px', y: '0px' };
    @ViewChild('roleContextMenuTrigger', { read: MatMenuTrigger }) roleContextMenuTrigger: MatMenuTrigger;

    //VNEXT-348: KLW - Context menu for workflow state buttons
    public workflowStateMenuPosition = { x: '0px', y: '0px' };
    @ViewChild('workflowStateMenuTrigger', { read: MatMenuTrigger }) workflowStateMenuTrigger: MatMenuTrigger;
    private workflowStateToDelete: WorkflowState = null;

    // Title and message properties.
    private readonly strWorkflowNeedsOneStateDialogueTitle: string = WorkflowComponentMessage.NO_WORKFLOW_STATE_DIALOGUE_TITLE;
    private readonly strWorkflowNeedsOneStateMessage: string = WorkflowComponentMessage.NO_WORKFLOW_STATE_DIALOGUE_MESSAGE;

    private hshWorkflowStateConnectorsByClientId: IWorkflowStateClientIdToWorkflowStateConnector = {};

    private readonly arrWorkflowStateTypes: WorkflowStateTypeInfo[] =
        [
            {
                typeId: 0, // An Id and an index into this array.

                typeName: WorkflowStateType.NOT_STARTED,
                typeColorName: WorkflowStateType.NOT_STARTED_COLOR,
                colorCssClass: WorkflowStateType.NOT_STARTED_CSS_CLASS
            },
            {
                typeId: 1, // An Id and an index into this array.

                typeName: WorkflowStateType.IN_PROGRESS,
                typeColorName: WorkflowStateType.IN_PROGRESS_COLOR,
                colorCssClass: WorkflowStateType.IN_PROGRESS_CSS_CLASS
            },
            {
                typeId: 2, // An Id and an index into this array.

                typeName: WorkflowStateType.COMPLETED,
                typeColorName: WorkflowStateType.COMPLETED_COLOR,
                colorCssClass: WorkflowStateType.COMPLETED_CSS_CLASS
            }
        ];

    private showPermissionsPreview: string = PermissionsSummaryEnum.None;
    private bShowWorkflowStatePopovers: boolean = false;
    private iShowPopoversDelayMs: number = 0;
    private iNumStatePopoversToOpen: number = 0;
    private iNumTransitionPopoversToOpen: number = 0;
    @ViewChildren(NgbPopover, { read: NgbPopover }) popovers: QueryList<NgbPopover>;


    //VNEXT-222: KLW - Add the variables for the later calculations
    @ViewChild('titleVertical') titleVertical: ElementRef;

    private actionBarWidth: number = 962;
    private letterWidth: number = 13;
    private offset: number = 5;
    public isVertical: boolean = false;

    // Constructor.
    public constructor(
        private formInstanceService: FormInstanceService,
        private route: ActivatedRoute,
        private dialog: MatDialog,
        private communicationService: CommunicationService,
        private workflowService: WorkflowService,
        private progressIndicatorService: ProgressIndicatorService,
        private dataCollectionService: DataCollectionService,
        private readonly titleService: Title,
        private currentUserService: CurrentUserService,
        private currentSiteService: CurrentSiteService,
        private appComponent: AppComponent, //VNEXT-235: KLW - Inject AppComponent
        elForBaseClass: ElementRef
    ) {
        super(elForBaseClass);
        let permissionsButtonNameMap: INameToPrettyNameMap =
        {
            'tbd': 'TBD',
        };
        this.element('#permissionButtonDiv').hasButtons().withNameToPrettyNameMap(permissionsButtonNameMap);
        // Added more metadata for more buttons here when time permits.
    }

    // Life cycle methods.
    public ngOnInit(): void {
        this.route.parent.data
            .subscribe((data: { currentSite: DataCollection }) => {
                this.site = data.currentSite;

                this.route.data
                    .subscribe((data: { workflow: Workflow }) => {
                        // Save a reference to the workflow.
                        this.workflow = data.workflow;
                        this.workflowLoaded(true);
                    });

                let url = this.route.parent.snapshot.url.concat(this.route.snapshot.url).join('/');
                this.communicationService.emitChangeToSkipNavLink(url);
            });

        this.titleService.setTitle(this.route.snapshot.data['title'] + ': ' + this.workflow.name);

        // Done.
        return;
    }

    public ngDoCheck(): void {
        // Get the height of the horizontal flexbox <div/>.
        let selHorizontalFlexBoxDiv = $('#horizontal-flexbox-div');
        let iHeight: number = selHorizontalFlexBoxDiv.height();

        //VNEXT-222: KLW - Take the actual width of the action bar, which is the parent of the title and see if it's less than the calculated max width before setting
        // the Material Toggle Buttons to vertical alignment
        if (this.titleVertical)
            if (this.titleVertical.nativeElement) {
                if (this.titleVertical.nativeElement.parentElement.offsetWidth < (this.actionBarWidth + (this.letterWidth * this.Workflow.name.length + this.offset))) {
                    this.isVertical = true;
                }
                else {
                    this.isVertical = false;
                }
            }

        return;
    }

    public ngAfterViewInit(): void {
        // List for the horizontal
        // flexbox div's resize event.
        let selHorFlexboxDiv = $('#horizontal-flexbox-div');
        selHorFlexboxDiv.resize(function () {
            console.log("horizontal-flexbox-div resized.");
        });
        let iHorFlexboxDivHeight: number = selHorFlexboxDiv.height();

        return;
    }

    // Implement the HasUnsavedChanges interface and protect against an unload with unsaved changes
    @HostListener('window:beforeunload', ['$event'])
    public handleBeforeUnload($event: any) {
        if (this.currentUserService.userIsLoggedIn && this.hasUnsavedChanges()) {
            $event.returnValue = "You have unsaved changes!";
        }
    }
    public hasUnsavedChanges(): boolean {
        if (environment.suppressUnsavedChangesWarning) {
            return false;
        }
        return this.isDirty;
    }

    public displayBuildContextMenu(event: any): void {
        this.buildContextMenuPosition.x = event.clientX + 'px';
        this.buildContextMenuPosition.y = event.clientY + 'px';

        this.buildContextMenuTrigger.menuData = { tbd: null };
        this.buildContextMenuTrigger.openMenu();
    }
    public displayConfigureRoleContextMenu(event: any, role: Role): void {
        this.roleContextMenuPosition.x = event.clientX + 'px';
        this.roleContextMenuPosition.y = event.clientY + 'px';

        this.roleContextMenuTrigger.menuData = { role: role };
        this.roleContextMenuTrigger.openMenu();
    }

    //VNEXT-348: KLW - Method to display context menu for workflow state buttons
    public displayWorkflowStateContextMenu(event: any, workflowState: WorkflowState): void {
        //VNEXT - 438: KLW - Don't show the context menu when workflow is default Site
        if (!this.ReadOnly) {
            this.workflowStateMenuPosition.x = event.clientX + 'px';
            this.workflowStateMenuPosition.y = event.clientY + 'px';

            this.workflowStateToDelete = workflowState;

            this.workflowStateMenuTrigger.menuData = { tbd: null };
            this.workflowStateMenuTrigger.openMenu();
        }
    }

    public contextMenuDeleteState() {
        if (this.workflowStateToDelete) {
            this.onWorkflowStateDroppedOnTrash({ dragData: this.workflowStateToDelete });
            this.workflowStateToDelete = null;
        }
    }

    public addNewStateAsFirst(event: any): void {
        if ((this.workflow.states != null) && (this.workflow.states.length > 0)) {
            let firstWorkflowState = this.workflow.states[0];

            this.onNewWorkflowStateDropped(event, firstWorkflowState, true);
        }
    }
    public addNewStateAsLast(event: any): void {
        if ((this.workflow.states != null) && (this.workflow.states.length > 0)) {
            let lastWorkflowState = this.workflow.states[this.workflow.states.length - 1];

            this.onNewWorkflowStateDropped(event, lastWorkflowState, false);
        }
    }

    public openConfigureRolesDialog(event: any): void {
        //VNEXT-348: KLW - Change to get current role
        let role = this.SiteRoles[0];

        this.sortWorkflowStates();
        this.sortWorkflowTransitionsForNewConfigureRolesDialogue();

        const dialogRef = this.dialog.open(ConfigureRolePermissionsDialog, {
            //VNEXT-306 KLW: Have the dialog automatically resize accordingly
            width: 'auto',
            data: {
                selectedRole: role,
                workflow: this.workflow,
                roles: this.SiteRoles
            }
        });

        dialogRef.afterClosed().subscribe(saveChanges => {
            // Note:  these permissions will get saved if/when
            //        the user clicks the workflow save button.
            /*
            if (saveChanges) {
                this.saveWorkflow();
            }
            */
        });

        //VNEXT-329 - KLW: Subscribe to the event to before the dialog is closed, which will return the flag if values
        // has been changed or not
        dialogRef.beforeClosed().subscribe(isDirty => {
            this.isDirty = isDirty;
        });
    }

    // Accessor methods.
    public get TrashCanTooltip(): string {
        let tooltip: string = "Drag a state or transition here to delete it";

        return (tooltip);
    }

    // Used to set the value of [dragScope] directives.
    // If current user can build workflows it returns the scope argument
    // otherwise it returns null, disabling drag/drop
    public DragScope(scope: string): any {
        let ret: any;
        if (this.workflow.CurrentUserCanBuildWorkflows) {
            ret = scope;
        } else {
            ret = null;
        }
        return ret;
    }

    //VNEXT-934: KLW - Hide and show properties when using the simple site permissions
    public get ShowOrHidePropertiesForSimplifiedPermissionsModel(): boolean {
        return this.currentSiteService.Site.useSimplifiedPermissionsModel;
    }

    public get StateOrTransitionBeingDragged(): boolean {
        let bStateBeingDragged: boolean = (this.iExistingWorkflowStateBeingDraggedIndex !== -1);
        let bTransitionBeingDragged: boolean = (this.workflowTransitionBeingDragged !== null);

        return (bStateBeingDragged || bTransitionBeingDragged);
    }

    public get BuildButtonTooltip(): string {
        let tooltip: string = '';

        if (!this.ReadOnly) {
            tooltip = "Use build mode to modify a workflow's structure";
        }

        return (tooltip);
    }

    public get PermissionsButtonTooltip(): string {
        let tooltip: string = '';

        if (!this.ReadOnly) {
            tooltip = "Use permissions mode to edit a workflow's permissions";
        }

        return (tooltip);
    }

    public get SummaryButtonTooltip(): string {
        let tooltip: string = '';

        if (!this.ReadOnly) {
            tooltip = "Use this mode to see a summary of assigned permissions";
        }

        return (tooltip);
    }

    public get SettingsButtonTooltip(): string {
        return ('Click this button to set the workflow name');
    }

    public get SaveButtonTooltip(): string {
        return ('Click this button to save the workflow');
    }

    public get PuzzlePieceTooltip(): string {
        return ('Drag this onto the canvas to create a new workflow state');
    }

    //VNEXT-222: KLW - Property to determine if the Material Toggle Button should be vertical
    public get IsVertical(): boolean {
        return this.isVertical;
    }

    public WorkflowStateButtonTooltip(workflowState: WorkflowState): string {
        let tooltip: string = `Workflow state '${workflowState.name}'`;

        return (tooltip);
    }

    public get TooltipPosition(): TooltipPosition {
        let position: TooltipPosition = 'below';

        return (position);
    }

    //VNEXT-348: KLW - Tooltip to show above the workflow state button
    public get TooltipPositionAbove(): TooltipPosition {
        let position: TooltipPosition = 'above';

        return (position);
    }

    public get Workflow(): Workflow {
        return (this.workflow);
    }

    public get ReadOnly(): boolean {
        return (this.workflow.isSystemWorkflow);
    }

    public get WorkflowStates(): WorkflowState[] {
        return (this.workflow.states);
    }

    public get ReversedWorkflowStates(): WorkflowState[] {
        let reversedWorkflowStates: WorkflowState[] = [];

        let iNumStatesToProcess: number = this.workflow.states.length;
        while (iNumStatesToProcess > 0) {
            iNumStatesToProcess--;

            let workflowState: WorkflowState = this.workflow.states[iNumStatesToProcess];

            reversedWorkflowStates.push(workflowState);
        }

        return (reversedWorkflowStates);
    }

    public get WorkflowStateCount(): number {
        return (this.workflow.states ? this.workflow.states.length : 0);
    }

    public IsLastWorkflowStateIndex(iStateIndex: number): boolean {
        let bIsLastState: boolean = (iStateIndex >= this.workflow.states.length - 1);

        return (bIsLastState);
    }

    public get WorkflowStatesByClientId(): IWorkflowStateIdToWorkflowState {
        return (this.hshWorkflowStatesByClientId);
    }

    public WorkflowStateCssClass(workflowState: WorkflowState): string {
        let strCssClasses: string;

        if (workflowState.isStartState) {
            strCssClasses = 'workflow-state-button not-started-workflow-state-button';
        } else if (workflowState.isEndState) {
            strCssClasses = 'workflow-state-button completed-workflow-state-button';
        } else {
            strCssClasses = 'workflow-state-button in-progress-workflow-state-button';
        }

        return (strCssClasses);
    }

    public get WorkflowTransitions(): WorkflowTransition[] {
        return (this.workflow ? this.workflow.transitions : []);
    }

    public get WorkflowTransitionCount(): number {
        return (this.workflow.transitions ? this.workflow.transitions.length : 0);
    }

    public get ForwardWorkflowTransitions(): WorkflowTransition[] {
        return (this.forwardWorkflowTransitions ? this.forwardWorkflowTransitions : []);
    }

    public get ShowBackwardTransitionGuidance(): boolean {
        let bShowGuidance: boolean =
            (this.ViewingMode == 'BuilderMode') &&
            this.WorkflowStates && (this.WorkflowStates.length > 1) &&
            (this.BackwardWorkflowTransitions.length == 0);

        return (bShowGuidance);
    }

    public get ShowForwardTransitionGuidance(): boolean {
        let bShowGuidance: boolean =
            (this.ViewingMode == 'BuilderMode') &&
            this.WorkflowStates && (this.WorkflowStates.length > 1) &&
            (this.ForwardWorkflowTransitions.length == 0);

        return (bShowGuidance);
    }

    public get BackwardWorkflowTransitions(): WorkflowTransition[] {
        return (this.backwardWorkflowTransitions ? this.backwardWorkflowTransitions : []);
    }

    public ForwardTransitionWorkflowStateDivClass(workflowTransition: WorkflowTransition, workflowState: WorkflowState): string {
        let cssClass: string = '';

        let startState: WorkflowState = this.getTransitionStartState(workflowTransition);
        let endState: WorkflowState = this.getTransitionEndState(workflowTransition);

        if (workflowTransition.transientStartStateClientId === workflowState.clientId) {
            cssClass = FORWARD_TRANSITION_START_STATE_DIV_CSS_CLASS;
        } else if (workflowTransition.transientEndStateClientId === workflowState.clientId) {
            cssClass = FORWARD_TRANSITION_END_STATE_DIV_CSS_CLASS;
        } else if ((workflowState.stateOrder > startState.stateOrder) &&
            (workflowState.stateOrder < endState.stateOrder)) {
            cssClass = FORWARD_TRANSITION_INTERMEDIATE_STATE_DIV_CSS_CLASS;
        } else {
            cssClass = FORWARD_TRANSITION_NA_STATE_DIV_CSS_CLASS;
        }

        return (cssClass);
    }

    public WorkflowStatesBeforeTransition(workflowTransition: WorkflowTransition, bForwardTransition: boolean): WorkflowState[] {
        // Do we need to calculate the states before this workflow transition?
        if (!workflowTransition.transientStatesBeforeTransition) {
            workflowTransition.transientStatesBeforeTransition = [];

            let startState: WorkflowState = this.getTransitionStartState(workflowTransition);

            for (let iState: number = 0; iState < this.workflow.states.length; iState++) {
                let workflowState: WorkflowState = this.workflow.states[iState];

                if (bForwardTransition && (workflowState.stateOrder < startState.stateOrder)) {
                    workflowTransition.transientStatesBeforeTransition.push(workflowState);
                } else if ((!bForwardTransition) && (workflowState.stateOrder > startState.stateOrder)) {
                    workflowTransition.transientStatesBeforeTransition.push(workflowState);
                }
            }
        }

        return (workflowTransition.transientStatesBeforeTransition);
    }

    public WorkflowStatesAfterTransition(workflowTransition: WorkflowTransition, bForwardTransition: boolean): WorkflowState[] {
        // Do we need to calculate the states after this workflow transition?
        if (!workflowTransition.transientStatesAfterTransition) {
            workflowTransition.transientStatesAfterTransition = [];

            let endState: WorkflowState = this.getTransitionEndState(workflowTransition);

            for (let iState: number = 0; iState < this.workflow.states.length; iState++) {
                let workflowState: WorkflowState = this.workflow.states[iState];

                if (bForwardTransition && (workflowState.stateOrder > endState.stateOrder)) {
                    workflowTransition.transientStatesAfterTransition.push(workflowState);
                } else if ((!bForwardTransition) && (workflowState.stateOrder < endState.stateOrder)) {
                    workflowTransition.transientStatesAfterTransition.push(workflowState);
                }
            }
        }

        return (workflowTransition.transientStatesAfterTransition);
    }

    public get HasWorkflowPermissions(): IHasWorkflowPermissions {
        return (this.hasWorkflowPermissions);
    }

    public get HasIdAndName(): IHasIdAndName {
        return (this.hasIdAndName);
    }

    public get IdAndNameObjectType(): string {
        return (this.idAndNameObjectType);
    }

    //VNEXT-348: KLW - Centralize the descriptions
    public get NameDescription() {
        let retVal: string = "";

        if (this.idAndNameObjectType == WorkflowEnum.WORKFLOW_STATE) {
            retVal = "<b>Hint:</b> Use adjectives for workflow state names.";
        }

        if (this.idAndNameObjectType == WorkflowEnum.WORKFLOW_ACTION) {
            retVal = "<b>Hint:</b> Use verbs for workflow actions.";
        }

        return retVal;
    }

    public get IsTransition(): boolean {
        return this.idAndNameObjectType == WorkflowEnum.WORKFLOW_ACTION;
    }

    public get IsState(): boolean {
        return this.idAndNameObjectType == WorkflowEnum.WORKFLOW_STATE;
    }

    public get IsWorkflow(): boolean {
        return this.idAndNameObjectType == WorkflowEnum.WORKFLOW;
    }

    public get HasWorkflowStateType(): boolean {
        return this.bHasWorkflowStateType;
    }
    public get HasWorkflowTransitionType(): boolean {
        return this.bHasWorkflowTransitionType;
    }

    public get WorkflowStateTypeId(): number {
        return ((<WorkflowState>this.hasIdAndName).transientWorkflowStateTypeId);
    }

    public set WorkflowStateTypeId(stateTypeIdParam: number) {
        (<WorkflowState>this.hasIdAndName).transientWorkflowStateTypeId = stateTypeIdParam;
        return;
    }

    public get WorkflowStateNotify(): boolean {
        let ws = <WorkflowState>this.hasIdAndName;
        if (ws) return ws.transientWorkflowStateNotify;
        return false;
    }

    public set WorkflowStateNotify(stateNotifyParam: boolean) {
        (<WorkflowState>this.hasIdAndName).transientWorkflowStateNotify = stateNotifyParam;
        return;
    }
    public get WorkflowStateShowSendNotificationsButton(): boolean {
        let ws = <WorkflowState>this.hasIdAndName;
        if (ws) return ws.transientWorkflowStateShowSendNotificationsButton;
        return false;
    }

    public set WorkflowStateShowSendNotificationsButton(stateNotifyParam: boolean) {
        (<WorkflowState>this.hasIdAndName).transientWorkflowStateShowSendNotificationsButton = stateNotifyParam;
        return;
    }

    public enableNotificationsAll(): void {
        for (let iState: number = 0; iState < this.workflow.states.length; iState++) {
            let workflowState: WorkflowState = this.workflow.states[iState];
            workflowState.notify = true;
        }
    }

    public removeNotificationsAll(): void {
        for (let iState: number = 0; iState < this.workflow.states.length; iState++) {
            let workflowState: WorkflowState = this.workflow.states[iState];
            workflowState.notify = false;
        }
    }

    public enableShowNotifyUsersButtonAll(): void {
        for (let iState: number = 0; iState < this.workflow.states.length; iState++) {
            let workflowState: WorkflowState = this.workflow.states[iState];
            workflowState.showSendNotificationsButton = true;
        }
    }
    public removeShowNotifyUsersButtonAll(): void {
        for (let iState: number = 0; iState < this.workflow.states.length; iState++) {
            let workflowState: WorkflowState = this.workflow.states[iState];
            workflowState.showSendNotificationsButton = false;
        }
    }

    public get WorkflowStateTypes(): WorkflowStateTypeInfo[] {
        return (this.arrWorkflowStateTypes);
    }

    public get ViewingMode(): string {
        return (this.viewingMode);
    }

    public get ViewingModeBuilderMode(): string {
        return (WorkflowViewingMode.BuilderMode);
    }

    public get ViewingModePermissionsMode(): string {
        return (WorkflowViewingMode.PermissionsMode);
    }

    public get ShowPermissionsPreviewValue(): string {
        return (this.showPermissionsPreview);
    }
    public get ShowNoPermissions(): string {
        return (PermissionsSummaryEnum.None);
    }
    public get ShowWorkflowStatePermissions(): string {
        return (PermissionsSummaryEnum.WORKFLOW_STATES);
    }
    public get ShowWorkflowTransitionPermissions(): string {
        return (PermissionsSummaryEnum.WORKFLOW_TRANSITIONS);
    }

    public get ShowPermissionsPopoversDelaysMs(): number {
        let iDelayMs: number =
            (this.showPermissionsPreview == PermissionsSummaryEnum.WORKFLOW_TRANSITIONS ? this.iShowPopoversDelayMs : 0);

        return (iDelayMs);
    }

    public get ShowTransitionPermissionsPreview(): boolean {
        let bShow: boolean = (this.showPermissionsPreview == PermissionsSummaryEnum.WORKFLOW_TRANSITIONS);

        return (bShow);
    }
    public get PermissionsPreviewButtonTooltip(): string {
        let tooltip: string = 'Toggle this to show/hide a summary of permissions';

        return (tooltip);
    }
    public get NoPermissionsTooltip(): string {
        let tooltip: string = 'Do not show a permissions summary';

        return (tooltip);
    }
    public get WorkflowStatePermissionsTooltip(): string {
        let tooltip: string = 'Show workflow state permission summaries';

        return (tooltip);
    }
    public get WorkflowTransitionPermissionsTooltip(): string {
        let tooltip: string = 'Show workflow state transition summaries';

        return (tooltip);
    }

    public WorkflowStatePermissionsPopoverTitle(workflowState: WorkflowState): string {
        let title: string = null;

        let iPermissionCount: number = workflowState.PermissionCount;
        if (iPermissionCount == 0) {
            title = `${workflowState.name} Permissions`;
        } else {
            title = `${workflowState.name} Permissions (${iPermissionCount})`;
        }

        return (title);
    }

    public GetPermissionNames(workflowState: IHasWorkflowPermissions, permissionData: WorkflowPermissionBase): string {
        let names: string = '';

        let permissionNames: string[] = workflowState.getPermissionNames();
        for (let iName: number = 0; iName < permissionNames.length; iName++) {
            let permissionName: string = permissionNames[iName];

            let propertyName: string = `can${permissionName}`;
            let bPermissionValue: boolean = permissionData[propertyName];

            if (bPermissionValue == true) {
                if (permissionName == 'BuildFormInstances') permissionName = 'Build FormsInstances'; // pharvey, 5/18/2021
                if (names != '') {
                    names += ', ';
                }

                names += permissionName;
            }
        }

        return (names);
    }

    public get PropertiesTitle(): string {
        return this.hasIdAndName != null ? this.hasIdAndName.getPropertiesDrawerTitle() : '';
    }

    //public get Notify(): boolean {
    //    return this.Workflow.notify;
    //}

    //public set Notify(value: boolean) {
    //    this.Workflow.notify = value;
    //}

    public get NameFieldToEdit(): string {
        let nameFieldToEdit: string = '';

        if (this.idAndNameObjectType) {
            let nameFieldParts = this.idAndNameObjectType.split(' ');

            for (let iPart: number = 0; iPart < nameFieldParts.length; iPart++) {
                let strPart: string = nameFieldParts[iPart];

                if (iPart > 0) {
                    nameFieldToEdit += ' ';
                }

                nameFieldToEdit += strPart.charAt(0).toUpperCase();
                nameFieldToEdit += strPart.slice(1);
            }

            nameFieldToEdit += ' Name';
        }

        return (nameFieldToEdit);
    }

    public get NameFieldValue(): string {
        return (this.hasIdAndName ? this.hasIdAndName.getName() : null);
    }
    public set NameFieldValue(strValueParam: string) {
        if (this.hasIdAndName) {
            this.hasIdAndName.setName(strValueParam);
        }

        return;
    }
    public get NameFieldTip(): string {
        return (this.nameFieldTip);
    }
    public nameFieldValueChanged(): void {
        return;
    }

    public get IsFolderWorkflow(): boolean {
        return this.workflow.isFolderWorkflow;
    }
    public set IsFolderWorkflow(value: boolean) {
        if (value) {
            this.workflow.isFormWorkflow = false;
            this.workflow.isFolderWorkflow = true;
        } else {
            this.workflow.isFormWorkflow = true;
            this.workflow.isFolderWorkflow = false;
        }
    }
    public get ShowIsFolderWorkflowCheckbox(): boolean {
        return !this.workflow.isDefaultFolderWorkflow && !this.workflow.isSystemWorkflow;
    }

    public get DescriptionFieldValue(): string {
        return (this.hasIdAndName != null ? this.hasIdAndName.getDescription() : null);
    }
    public set DescriptionFieldValue(val: string) {
        if (this.hasIdAndName != null)
            this.hasIdAndName.setDescription(val);
    }
    public descriptionFieldValueChanged(): void {
        return;
    }

    public get ChangeWorkflowStateDialogTitle(): string {
        return this.hasIdAndName != null ? this.hasIdAndName.getChangeWorkflowStateDialogTitle() : null;
    }
    public set ChangeWorkflowStateDialogTitle(value: string) {
        if (this.hasIdAndName != null)
            this.hasIdAndName.setChangeWorkflowStateDialogTitle(value);
    }
    public changeWorkflowStateDialogTitleChanged(): void {
        // Note:  this method is a NOOP by design.
    }

    public get TransitionConfirmationDialogMessage(): string {
        return this.hasIdAndName != null ? this.hasIdAndName.getTransitionConfirmationDialogMessage() : null;
    }
    public set TransitionConfirmationDialogMessage(value: string) {
        if (this.hasIdAndName != null)
            this.hasIdAndName.setTransitionConfirmationDialogMessage(value);
    }
    public transitionConfirmationDialogMessagChanged(): void {
        // Note:  this method is a NOOP by design.
    }

    public get TakeUserToSiteHomePageAfterTransitionApplied(): boolean {
        return this.hasIdAndName != null ? this.hasIdAndName.getTakeUserToSiteHomePageAfterTransitionApplied() : false;
    }
    public set TakeUserToSiteHomePageAfterTransitionApplied(value: boolean) {
        if (this.hasIdAndName != null)
            this.hasIdAndName.setTakeUserToSiteHomePageAfterTransitionApplied(value);
    }
    public takeUserToSiteHomePageAfterTransitionAppliedChange(): void {
        // Note:  this method is a NOOP by design.
    }

    public get BuilderToolsDivId(): string {
        return (this.divIds.strBuilderToolsDivId);
    }

    public get RolesDivId(): string {
        return (this.divIds.strRolesDivId);
    }

    public get TrashDivId(): string {
        return (this.divIds.strTrashDivId);
    }

    public get WorkflowStateToolMatIconName(): string {
        return ('api'); // Note:  this will have to change to something more suitable.        
    }

    public get WorkflowStateToolSvgIconName(): string {
        return ('puzzle_piece');
    }

    public get SiteRoles(): Role[] {
        let result: Role[] = this.site.roles;

        if (this.site.roles && (this.site.roles.length > 6)) {
        }

        return (result);
    }

    public get RoleToolMatIconName(): string {
        return ('person'); // Note:  this will have to change to something more suitable.
    }

    public get BuildContextMenuPositionX(): string {
        return (this.buildContextMenuPosition.x);
    }
    public get BuildContextMenuPositionY(): string {
        return (this.buildContextMenuPosition.y);
    }
    public get RoleContextMenuPositionX(): string {
        return (this.roleContextMenuPosition.x);
    }
    public get RoleContextMenuPositionY(): string {
        return (this.roleContextMenuPosition.y);
    }

    //VNEXT-348: KLW - Positions for the workflow state context menu
    public get WorkflowStateMenuPositionX(): string {
        return (this.workflowStateMenuPosition.x);
    }
    public get WorkflowStateMenuPositionY(): string {
        return (this.workflowStateMenuPosition.y);
    }

    // Handle control events.
    public workflowStateClicked(ignoredEvent: any, workflowState: WorkflowState, ignoredPopover: NgbPopover): void {
        AccessibilityHelper.setFocus('#drawer-keyboard-target-div');

        //VNEXT-392: KLW - Add missing roles
        workflowState = this.setMissingStatePermissions(workflowState, this.SiteRoles, false);

        workflowState.transientWorkflowStateNotify = workflowState.notify;
        workflowState.transientWorkflowStateShowSendNotificationsButton = workflowState.showSendNotificationsButton;

        // Save an undo drawer operation operator.
        this.drawerCancelledOperator = new WorkflowObjectEditCancelled(workflowState);

        // Prepare and open the appropriate drawer contents.
        if (this.viewingMode == WorkflowViewingMode.PermissionsMode) {
            this.hasWorkflowPermissions = workflowState;
        } else if (this.viewingMode == WorkflowViewingMode.BuilderMode) {
            this.hasWorkflowPermissions = workflowState;

            // Set attributes.
            this.hasIdAndName = workflowState;
            this.idAndNameObjectType = WorkflowEnum.WORKFLOW_STATE;
            this.nameFieldTip = 'Edit workflow state name';

            this.bHasWorkflowStateType = true;
            this.bHasWorkflowTransitionType = false;
            {
                // Determine/set the workflow state's type Id.
                let arrWorkflowStateType: WorkflowStateTypeInfo[];
                if (workflowState.isStartState) {
                    arrWorkflowStateType =
                        this.arrWorkflowStateTypes.filter(wst => wst.typeName == WorkflowStateType.NOT_STARTED);
                } else if (workflowState.isEndState) {
                    arrWorkflowStateType =
                        this.arrWorkflowStateTypes.filter(wst => wst.typeName == WorkflowStateType.COMPLETED);
                } else {
                    arrWorkflowStateType =
                        this.arrWorkflowStateTypes.filter(wst => wst.typeName == WorkflowStateType.IN_PROGRESS);
                }

                workflowState.transientWorkflowStateTypeId = arrWorkflowStateType[0].typeId;
            }
        }

        // Open the drawer.
        this.multiPurposeDrawer.open();

        return;
    }

    public permissionsPopoverShown(hasIdAndName: IHasIdAndName): void {
        // In case the workflow state div has
        // not yet been heightened, do it now.
        this.heightenWorkflowStatesDiv();

        // Keep track of popovers yet to open.
        if (hasIdAndName.typeName() == WorkflowState.TypeName) {
            if (this.iNumStatePopoversToOpen > 0) {
                this.iNumStatePopoversToOpen--;
            }
        } else if (hasIdAndName.typeName() == WorkflowTransition.TypeName) {
            if (this.iNumTransitionPopoversToOpen > 0) {
                this.iNumTransitionPopoversToOpen--;
            }
        }

        return;
    }

    public permissionsPopoverHidden(unusedObject: IHasIdAndName): void {
        // If any popover is hidden for any reason,
        // reset the show permissions summary to 'None'.
        if (this.showPermissionsPreview != PermissionsSummaryEnum.None) {
            if ((this.iNumStatePopoversToOpen == 0) &&
                (this.iNumTransitionPopoversToOpen == 0)) {
                this.showPermissionsPreview = PermissionsSummaryEnum.None;

                this.shortenWorkflowStatesDiv();
            }
        }

        return;
    }

    private heightenWorkflowStatesDiv(): void {
        let workflowStatesDiv = $('#workflow-states-div');
        if (workflowStatesDiv.hasClass('shorten-workflow-states-div')) {
            workflowStatesDiv.removeClass('shorten-workflow-states-div');
        }
        if (!workflowStatesDiv.hasClass('heighten-workflow-states-div')) {
            workflowStatesDiv.addClass('heighten-workflow-states-div');
        }

        return;
    }

    private shortenWorkflowStatesDiv(): void {
        let workflowStatesDiv = $('#workflow-states-div');
        if (workflowStatesDiv.hasClass('heighten-workflow-states-div')) {
            workflowStatesDiv.removeClass('heighten-workflow-states-div');
        }
        if (!workflowStatesDiv.hasClass('shorten-workflow-states-div')) {
            workflowStatesDiv.addClass('shorten-workflow-states-div');
        }

        return;
    }

    // Define onWorkflowTransitionStateClicked() that acts as an event handler
    // and wrapper for calling the next method, workflowStateTransitionClicked().
    public onWorkflowTransitionClicked(eventData: any): void {
        let workflowTransition: WorkflowTransition = eventData['workflowTransition'];
        let workflowState: WorkflowState = eventData['workflowState'];
        let bIsVerticalStateConnector = eventData['isVerticalStateConnector'];
        this.workflowTransitionClicked(workflowTransition, workflowState, bIsVerticalStateConnector);

        return;
    }

    //public workflowTransitionStateClicked(workflowTransition: WorkflowTransition,
    public workflowTransitionClicked(workflowTransition: WorkflowTransition,
        workflowState: WorkflowState,
        bIsVerticalStateConnector = false): void {
        // See if we should handle this click.
        this.bHasWorkflowTransitionType = true;
        this.bHasWorkflowStateType = false;
        let bRespondToClick: boolean = false;

        if (workflowState) {
            if (bIsVerticalStateConnector) {
                // We have a vertical connector.
                let verticalStateConnectorCssClass: string =
                    this.ForwardTransitionWorkflowStateDivClass(workflowTransition, workflowState);

                if (verticalStateConnectorCssClass != FORWARD_TRANSITION_NA_STATE_DIV_CSS_CLASS) {
                    bRespondToClick = true;
                }
            } else {
                // We have a horizontal state-to-state transition connection.
                let startState: WorkflowState = this.getTransitionStartState(workflowTransition);
                let endState: WorkflowState = this.getTransitionEndState(workflowTransition);

                if (workflowState.id == workflowTransition.startStateId) {
                    bRespondToClick = true;
                } else if ((workflowState.stateOrder > startState.stateOrder) &&
                    (workflowState.stateOrder < endState.stateOrder)) {
                    bRespondToClick = true;
                }
            }
        } else {
            bRespondToClick = true;
        }

        // Should we respond to this click?
        if (bRespondToClick) {
            AccessibilityHelper.setFocus('#drawer-keyboard-target-div');

            // Save an undo drawer operation operator.
            this.drawerCancelledOperator = new WorkflowObjectEditCancelled(workflowTransition);

            // Prepare and open the appropriate drawer contents.
            if (this.viewingMode == WorkflowViewingMode.PermissionsMode) {
                this.hasWorkflowPermissions = workflowTransition;
                this.multiPurposeDrawer.open();
            } else if (this.viewingMode == WorkflowViewingMode.BuilderMode) {
                this.hasWorkflowPermissions = workflowTransition;

                this.hasIdAndName = workflowTransition;
                this.idAndNameObjectType = WorkflowEnum.WORKFLOW_ACTION;

                this.multiPurposeDrawer.open();
            }
        }

        return;
    }

    public workflowStateTypeIdChanged(): void {
        let workflowState: WorkflowState = (<WorkflowState>this.hasIdAndName);
        let iWorkflowStateTypeId = workflowState.transientWorkflowStateTypeId;

        let workflowStateTypeInfo = this.arrWorkflowStateTypes[iWorkflowStateTypeId];

        if (workflowStateTypeInfo.typeName == WorkflowStateType.NOT_STARTED) {
            workflowState.isStartState = true;
            workflowState.isEndState = false;
        } else if (workflowStateTypeInfo.typeName == WorkflowStateType.COMPLETED) {
            workflowState.isStartState = false;
            workflowState.isEndState = true;
        } else {
            workflowState.isStartState = false;
            workflowState.isEndState = false;
        }

        return;
    }

    public workflowStateNotifyChanged(): void {
        let workflowState: WorkflowState = (<WorkflowState>this.hasIdAndName);
        workflowState.notify = !workflowState.notify;
        return;
    }
    public workflowStateShowNotifyUsersButtonChanged(): void {
        let workflowState: WorkflowState = (<WorkflowState>this.hasIdAndName);
        workflowState.showSendNotificationsButton = !workflowState.showSendNotificationsButton;
        return;
    }

    public closeDrawer(e: any): void {
        // Close the drawer.
        this.multiPurposeDrawer.close();

        return;
    }

    public drawerClosing(): void {
        // Cancel any unsaved action.
        //
        // Note:  method savePropertiesClicked()
        //        get called before this method.
        this.cancelDrawerAction();

        // Reset drawer-related instance data.
        if (this.hasWorkflowPermissions) {
            // Reset any 'transientIsExpanded' value.
            let permissions: WorkflowPermissionBase[] = this.hasWorkflowPermissions.getPermissions();
            if (permissions && (permissions.length > 0)) {
                for (let iPermission: number = 0; iPermission < permissions.length; iPermission++) {
                    let permission: WorkflowPermissionBase = permissions[iPermission];

                    permission.transientIsExpanded = false;
                }
            }

            this.hasWorkflowPermissions = null;
        }


        this.hasIdAndName = null;
        this.idAndNameObjectType = null;
        this.bHasWorkflowStateType = false;

        return;
    }

    public workflowModeClicked(eventData: any): void {
        let builderToolsDiv = $('#' + this.divIds.strBuilderToolsDivId);
        let rolesDiv = $('#' + this.divIds.strRolesDivId);
        let trashDiv = $('#' + this.divIds.strTrashDivId);

        if (eventData) {
            if (eventData['value'] != this.viewingMode) {
                if (eventData['value'] == WorkflowViewingMode.BuilderMode) {
                    this.viewingMode = WorkflowViewingMode.BuilderMode;

                    builderToolsDiv.removeClass(WorkflowCssClassNameEnum.HIDE_BUILDER_TOOLS_DIV);
                    builderToolsDiv.addClass(WorkflowCssClassNameEnum.SHOW_BUILDER_TOOLS_DIV);

                    rolesDiv.addClass(WorkflowCssClassNameEnum.HIDE_ROLES_DIV);

                    trashDiv.removeClass(WorkflowCssClassNameEnum.HIDE_TRASH_DIV);
                    trashDiv.addClass(WorkflowCssClassNameEnum.SHOW_TRASH_DIV);
                } else if (eventData['value'] == WorkflowViewingMode.PermissionsMode) {
                    this.viewingMode = WorkflowViewingMode.PermissionsMode;

                    builderToolsDiv.removeClass(WorkflowCssClassNameEnum.SHOW_BUILDER_TOOLS_DIV);
                    builderToolsDiv.addClass(WorkflowCssClassNameEnum.HIDE_BUILDER_TOOLS_DIV);

                    rolesDiv.removeClass(WorkflowCssClassNameEnum.HIDE_ROLES_DIV);
                    rolesDiv.addClass(WorkflowCssClassNameEnum.SHOW_ROLES_DIV);
                }
            } // if
        } // if

        return;
    }

    public permissionsPreviewToggled(eventData: any): void {
        let priorShowPermissionsPreviewValue: string = this.showPermissionsPreview;
        this.showPermissionsPreview = eventData['value'];

        let bHeightenWorkflowStateDiv: boolean = (priorShowPermissionsPreviewValue == PermissionsSummaryEnum.None);
        this.iShowPopoversDelayMs = (bHeightenWorkflowStateDiv ? 500 : 0);

        let bTriggerWorkflowStatePopovers: boolean = false;
        this.bShowWorkflowStatePopovers = false; // Will reset to true soon if needed.

        if (this.showPermissionsPreview == PermissionsSummaryEnum.WORKFLOW_STATES) {
            this.bShowWorkflowStatePopovers = true;
            bTriggerWorkflowStatePopovers = true;
            bHeightenWorkflowStateDiv = true; // Cannot hurt to do this if the div is already heighted.

            this.iNumStatePopoversToOpen = this.WorkflowStates.length;
            this.iNumTransitionPopoversToOpen = 0;
        } else if (this.showPermissionsPreview == PermissionsSummaryEnum.WORKFLOW_TRANSITIONS) {
            if (priorShowPermissionsPreviewValue == PermissionsSummaryEnum.WORKFLOW_STATES) {
                bTriggerWorkflowStatePopovers = true;
            }

            bHeightenWorkflowStateDiv = true; // Cannot hurt to do this if the div is already heighted.

            this.iNumTransitionPopoversToOpen =
                (this.BackwardWorkflowTransitions.length + this.ForwardWorkflowTransitions.length);
            this.iNumStatePopoversToOpen = 0;
        } else if (priorShowPermissionsPreviewValue == PermissionsSummaryEnum.WORKFLOW_STATES) {
            // Need to trigger the closing of these popovers.
            bTriggerWorkflowStatePopovers = true;
        }

        if (bHeightenWorkflowStateDiv) {
            this.heightenWorkflowStatesDiv();
        } else if (this.showPermissionsPreview == PermissionsSummaryEnum.None) {
            this.shortenWorkflowStatesDiv();
        }

        if (bTriggerWorkflowStatePopovers) {
            setTimeout(() => {
                this.delayedTriggerPopovers();
            }, this.iShowPopoversDelayMs);
        }

        return;
    }

    private delayedTriggerPopovers(): void {
        if ((this.popovers != null) && (this.popovers.length > 0)) {
            let arrPopovers: NgbPopover[] = this.popovers.toArray();

            for (let iPopover: number = 0; iPopover < arrPopovers.length; iPopover++) {
                let popover: NgbPopover = arrPopovers[iPopover];

                if (this.bShowWorkflowStatePopovers) {
                    popover.open();
                } else if (popover.isOpen()) {
                    popover.close();
                }
            }
        }

        return;
    }

    // Define methods related to drag and drop.
    public createDraggableWorkflowStateConnector(workflowState: WorkflowState): WorkflowStateConnector {
        if ((!workflowState.clientId) || (workflowState.clientId == 0)) {
            this.throwError('createDraggableWorkflowStateConnector():  encountered a workflow state without a client Id.');
        }

        let workflowStateConnector: WorkflowStateConnector =
            this.hshWorkflowStateConnectorsByClientId[workflowState.clientId];

        if (workflowStateConnector == null) {
            workflowStateConnector = new WorkflowStateConnector(workflowState);

            this.hshWorkflowStateConnectorsByClientId[workflowState.clientId] = workflowStateConnector;
        }

        return (workflowStateConnector);
    }

    public isWorkflowStateConnectorTarget(workflowState: WorkflowState): boolean {
        let bIsTarget: boolean = (this.workflowStateConnectorBeingDragged != null) &&
            (this.workflowStateConnectorBeingDragged.name != workflowState.name);

        return (bIsTarget);
    }

    // Define an event handler, onTransitionDragDropAction() method
    // that is a wrapper for calling the following methods:
    //
    //     1. onExistingWorkflowTransitionDragStart();
    //     2. onExistingWorkflowTransitionDragEnd(); and
    //     3. onRoleDroppedOnWorkflowTransition().
    public onTransitionDragDropAction(eventData: any): void {
        let dragDropAction = eventData['action'];
        let originalEvent = eventData['event'];
        let workflowTransition = eventData['workflowTransition'];

        if (dragDropAction == 'dragStart') {
            this.onExistingWorkflowTransitionDragStart(originalEvent, workflowTransition);
        } else if (dragDropAction == 'dragEnd') {
            this.onExistingWorkflowTransitionDragEnd(originalEvent);
        } else if (dragDropAction == 'drop') {
            this.onRoleDroppedOnWorkflowTransition(originalEvent, workflowTransition);
        }

        return;
    }

    public onExistingWorkflowTransitionDragStart(eventData: any,
        workflowTransition: WorkflowTransition): void {
        this.workflowTransitionBeingDragged = workflowTransition;

        return;
    }

    public onExistingWorkflowTransitionDragEnd(eventData: any): void {
        this.workflowTransitionBeingDragged = null;

        return;
    }

    public onExistingWorkflowStateDragStart(eventData: any,
        workflowState: WorkflowState,
        iWorkflowStateIndex: number): void {
        this.iExistingWorkflowStateBeingDraggedIndex = iWorkflowStateIndex;

        return;
    }

    public onExistingWorkflowStateDragEnd(): void {
        this.iExistingWorkflowStateBeingDraggedIndex = -1;

        return;
    }

    public onWorkflowStateConnectorDragStart(eventData: any,
        workflowState: WorkflowState,
        iWorkflowStateIndex: number): void {
        this.workflowStateConnectorBeingDragged = workflowState;
        this.iDraggedWorkflowStateConnectorIndex = iWorkflowStateIndex;

        return;
    }

    public onWorkflowStateConnectorDrag(eventData: any,
        workflowState: WorkflowState,
        iWorkflowStateIndex: number): void {

        return;
    }

    public onWorkflowStateConnectorDragEnd(eventData: any,
        workflowState: WorkflowState,
        iWorkflowStateIndex: number): void {

        this.workflowStateConnectorBeingDragged = null;
        this.iDraggedWorkflowStateConnectorIndex = -1;

        return;
    }

    public onWorkflowStateDragEnter(event: any,
        workflowState: WorkflowState,
        iWorkflowStateIndex: number): void {
        // If a workflow state connection is being dragged,
        // see if that connect can be dropped on this state.

        return;
    }

    public onWorkflowStateDragOver(event: any,
        workflowState: WorkflowState,
        iWorkflowStateIndex: number): void {
        // TO DO:  code this method.

        return;
    }

    public onWorkflowStateDragLeave(event: any,
        workflowState: WorkflowState,
        iWorkflowStateIndex: number): void {
        // Reset the flag indicating that the
        // connector is over a different workflow state.

        return;
    }

    // Note:  method dropOnWorkflowStateAllowed(), next, has to be an arrow function.
    public dropOnWorkflowStateAllowed = (dragData: any): boolean => {
        let bDropAllowed: boolean = false;

        if (dragData) {
            let strTypeName: string = dragData.constructor.name;

            switch (strTypeName) {
                case 'Role':
                    bDropAllowed = true;
                    break;

                case 'WorkflowState':
                    bDropAllowed = true;
                    break;

                case 'WorkflowStateConnector':
                    bDropAllowed = false;
                    break;

                default:
                    break;
            } // switch
        } // if        

        return (bDropAllowed);
    }

    public onItemDroppedOnWorkflowState(eventData: any,
        workflowState: WorkflowState,
        iWorkflowStateIndex: number): void {
        let draggedRoleOrNull: Role = eventData['dragData'];

        if (draggedRoleOrNull) {
            // Save an undo drawer operation operator.
            this.drawerCancelledOperator = new WorkflowObjectEditCancelled(workflowState);

            // The user dragged a role onto a workflow state.
            // If the workflow state does not have a permission
            // record for the dropped role, add one now.
            let rolePermissions: WorkflowStatePermission[] =
                (workflowState.permissions ?
                    workflowState.permissions.filter(p => p.roleName == draggedRoleOrNull.name) :
                    null);

            if ((!rolePermissions) || (rolePermissions.length == 0)) {
                let rolePermission: WorkflowStatePermission = new WorkflowStatePermission();
                rolePermission.roleId = draggedRoleOrNull.id;
                rolePermission.roleName = draggedRoleOrNull.name;

                workflowState.permissions.push(rolePermission);
            }

            // Configure and open the drawer.
            this.hasWorkflowPermissions = workflowState;

            setTimeout(() => {
                this.openMultipurposeDrawer();
            }, 300);
        } else {
            // The user dragged the workflow state
            // tool onto another workflow state.
        }
        this.flagDirty();

        return;
    }

    public onRoleDroppedOnWorkflowTransition(eventData: any, workflowTransition: WorkflowTransition): void {
        let role: Role = eventData['dragData'];

        // Save an undo drawer operation operator.
        //this.drawerCancelledOperator = new WorkflowTransitionEditCancelled(workflowTransition);
        this.drawerCancelledOperator = new WorkflowObjectEditCancelled(workflowTransition);

        // The user dragged a role onto a workflow state.
        // If the workflow state does not have a permission
        // record for the dropped role, add one now.
        let rolePermissions: WorkflowTransitionPermission[] = null;

        if (workflowTransition.permissions != null) {
            rolePermissions = workflowTransition.permissions.filter(p => p.roleName == role.name);
        } else {
            workflowTransition.permissions = [];
        }

        if ((!rolePermissions) || (rolePermissions.length == 0)) {
            let rolePermission: WorkflowTransitionPermission = new WorkflowTransitionPermission();
            rolePermission.roleId = role.id;
            rolePermission.roleName = role.name;

            rolePermission.canEdit = true; // Used as a standin/synonum for 'canTransition'.

            workflowTransition.permissions.push(rolePermission);
        }

        // Configure and open the drawer.
        this.hasWorkflowPermissions = workflowTransition;

        this.multiPurposeDrawer.open();
        this.flagDirty();

        return;
    }

    public onWorkflowStateConnectorDroppedOnWorkflowState(eventData: any,
        workflowState: WorkflowState,
        iWorkflowStateIndex: number): void {
        if (this.workflowStateConnectorBeingDragged) {
            if (iWorkflowStateIndex != this.iDraggedWorkflowStateConnectorIndex) {
                // Get the start and end states.
                let startWorkflowState: WorkflowState = this.workflow.states[this.iDraggedWorkflowStateConnectorIndex];
                let endWorkflowState: WorkflowState = this.workflow.states[iWorkflowStateIndex];

                // Make sure a transition between these states
                // in this direction does not already exist.
                if (this.workflow.transitions && (this.workflow.transitions.length > 0)) {
                    let arrExistingTransition =
                        this.workflow.transitions.filter(t =>
                            (t.transientStartStateClientId == startWorkflowState.clientId) &&
                            (t.transientEndStateClientId == endWorkflowState.clientId));

                    if (arrExistingTransition && (arrExistingTransition.length > 0)) {
                        // A transition between these states in this direction
                        // already exists, so display an error message.
                        let dialogueTitle: string = "Transition Already Exists";
                        let dialogueMessage: string = `A workflow transition from workflow state '${startWorkflowState.name}' ` +
                            `to state '${endWorkflowState.name}' already exists.`;

                        let naDialogueRef = this.dialog.open(AlertDialogComponent, {
                            maxWidth: "400px",
                            data: new AlertDialogModel(dialogueTitle, dialogueMessage, null)
                        });

                        return;
                    }
                }

                // Generate a transition id.
                let iNewTransitionId: number = 1;
                if (this.workflow.transitions && (this.workflow.transitions.length > 0)) {
                    iNewTransitionId = this.workflow.transitions.length + 1;
                } else {
                    this.workflow.transitions = [];
                }

                // Create the new transition.
                let strNewTransitionName: string =
                    this.generateNewTransitionName(startWorkflowState, endWorkflowState);

                let newTransition: WorkflowTransition = new WorkflowTransition();
                newTransition.name = strNewTransitionName;
                newTransition.startStateId = startWorkflowState.id;
                newTransition.endStateId = endWorkflowState.id;
                newTransition.startStateName = startWorkflowState.name;
                newTransition.endStateName = endWorkflowState.name;
                newTransition.transientStartStateClientId = startWorkflowState.clientId;
                newTransition.transientEndStateClientId = endWorkflowState.clientId;
                newTransition.clientId = this.getNextUniqueWorkflowTransitionClientId();

                //VNEXT-297 KLW: Add the new permission
                let perm = this.createPermissionFromEndState(endWorkflowState);

                if (perm)
                    newTransition.permissions = perm;

                this.workflow.transitions.push(newTransition);

                this.loadForwardAndBackwardTransitions();
            }

            this.workflowStateConnectorBeingDragged = null;

            this.flagDirty();
        }

        return;
    }


    public workflowStatePermissionChanged(permission: WorkflowStatePermission, permissionName: string): void {
        // If either all of the permission flags are set or none are
        // set, update the 'All' permissions checkbox value accordingly.
        if (permission.canView && permission.canEdit && permission.canDelete && permission.canGrant && permission.canBuildFolders && permission.canBuildFormInstances) {
            permission.canDoAll = true;
        } else if ((!permission.canView) && (!permission.canEdit) && (!permission.canDelete) && (!permission.canGrant) && (!permission.canBuildFolders) && (!permission.canBuildFormInstances)) {
            permission.canDoAll = false;
        }

        //VNEXT-329 - KLW: Set the flag to show that values have been changed
        this.flagDirty();
    }

    //VNEXT-297 KLW: Method to calculate the new permisison based off the current permissons of the Workflow State
    private createPermissionFromEndState(passedEndState: WorkflowState): WorkflowTransitionPermission[] {

        let retVal: WorkflowTransitionPermission[] = [];

        passedEndState.permissions.forEach(y => {

            let rolePermission: WorkflowTransitionPermission = new WorkflowTransitionPermission();
            rolePermission.roleId = y.roleId;
            rolePermission.roleName = y.roleName;

            rolePermission.canEdit = true;

            //VNEXT-348: KLW - Change the permissions to infer the action permission
            if (y.canView == true ||
                y.canEdit == true ||
                y.canGrant == true ||
                y.canDelete == true ||
                y.canBuildFolders == true ||
                y.canBuildFormInstances == true) {
                //Add and set the permission as canView
                rolePermission.canView = true;
            }
            else {
                rolePermission.canView = false;
            }

            retVal.push(rolePermission);
        });

        return retVal;

    }

    public onWorkflowStateDroppedBeforeState(eventData: any, workflowState: WorkflowState): void {
        if (this.iExistingWorkflowStateBeingDraggedIndex >= 0) {
            this.onExistingWorkflowStateDropped(eventData, workflowState, true);
        } else {
            this.onNewWorkflowStateDropped(eventData, workflowState, true);
        }

        // Make sure the drag end logic executes (the event
        // handler is not getting called on a drag end).
        this.onExistingWorkflowStateDragEnd();

        // Reassess the workflow's makeup.
        this.loadForwardAndBackwardTransitions();

        // Mark the workflow as having changed.
        this.flagDirty();

        // Done.
        return;
    }

    public onWorkflowStateDroppedAfterState(eventData: any, workflowState: WorkflowState): void {
        if (this.iExistingWorkflowStateBeingDraggedIndex >= 0) {
            this.onExistingWorkflowStateDropped(eventData, workflowState, false);
        } else {
            this.onNewWorkflowStateDropped(eventData, workflowState, false);
        }

        // Make sure the drag end logic executes (the event
        // handler is not getting called on a drag end).
        this.onExistingWorkflowStateDragEnd();

        // Reassess the workflow's makeup.
        this.loadForwardAndBackwardTransitions();

        // Mark the workflow as having changed.
        this.flagDirty();

        // Done.
        return;
    }


    public onObjectDroppedOnTrash(eventData: any): void {

        // Get the drag object and determine its type.
        let object: any = eventData['dragData'];

        let hasTypeAndName: IHasIdAndName = <IHasIdAndName>object;
        let objectType = hasTypeAndName.typeName();

        if (objectType == 'WorkflowState') {
            this.onWorkflowStateDroppedOnTrash(eventData);
        } else if (objectType == 'WorkflowTransition') {
            this.onWorkflowTransitionDroppedOnTrash(eventData);
        }

        // Make sure the drag end logic executes (the event
        // handler is not getting called on a drag end).
        this.onExistingWorkflowStateDragEnd();

        // Mark the workflow as having changed.
        this.flagDirty();

        return;
    }

    //VNEXT-372: KLW - Return response from web service call
    private findFormInstancesUsingWorkflowState(id: number) {

        return new Promise<FormInstancesAndFoldersUsingWorkflows>((resolve) => {
            this.formInstanceService.getFormInstancesUsingWorkflowState(id).then((response: FormInstancesAndFoldersUsingWorkflows) => {
                if (response.fI_Names) {
                    let dialogConfig: MatDialogConfig = new MatDialogConfig();
                    dialogConfig.data = response;
                }

                resolve(response);
            });
        });
    }

    private onWorkflowStateDroppedOnTrash(eventData: any): void {

        let workflowStateToDelete: WorkflowState = eventData['dragData'];

        //VNEXT-372: KLW - Put in logic to account for different scenarios of deleting workflow states
        this.findFormInstancesUsingWorkflowState(workflowStateToDelete.getId()).then(x => {

            // Create a confirmation or alert message based
            // on what state was dragged to the trash/deleted.
            let showAlertDialog: boolean = false;
            let bWorkflowHasBeenSaved: boolean = (this.workflow.id > 0);
            let confirmationMessage: string = null;
            let alertMessage: string = null;
            let moveAnyObjectsText: string = `Please move any forms or folders in workflow state '${workflowStateToDelete.name}' `;
            let stateToMoveObjectsInDeletedState: WorkflowState = null;
            let newStartState: WorkflowState = null;
            let newEndState: WorkflowState = null;

            let remainingStates: WorkflowState[] = this.workflow.states.filter(ws => ws.clientId != workflowStateToDelete.clientId);
            let existingStartStates: WorkflowState[] = this.workflow.states.filter(ws => ws.isStartState);
            let existingEndStates: WorkflowState[] = this.workflow.states.filter(ws => ws.isEndState);

            if (this.workflow.states.length == 1) {
                alertMessage = `Workflow state '${workflowStateToDelete.name}' cannot be deleted as it is the workflow's only state.`;
                showAlertDialog = true;
            } else if (this.workflow.states.length == 2) {
                let remainingState: WorkflowState = this.workflow.states.find(ws => ws.clientId != workflowStateToDelete.clientId);

                newStartState = remainingState;
                newEndState = remainingState;

                if (bWorkflowHasBeenSaved && (workflowStateToDelete.id > 0)) {
                    stateToMoveObjectsInDeletedState = remainingState;

                    confirmationMessage = moveAnyObjectsText + `to state '${remainingState.name}', make the latter the workflow's start and end state, and save the workflow.`;
                } else
                    confirmationMessage = `Please make state '${remainingState.name}' the workflow's start and end state.`;
            } else if (workflowStateToDelete.isStartState && (existingStartStates.length == 1)) {

                let firstRemainingState: WorkflowState = remainingStates[0];

                newStartState = firstRemainingState;

                if (bWorkflowHasBeenSaved && (workflowStateToDelete.id > 0)) {
                    stateToMoveObjectsInDeletedState = firstRemainingState;

                    confirmationMessage = moveAnyObjectsText + `to state '${firstRemainingState.name}', make the latter the start state, and save the workflow.`;
                } else
                    confirmationMessage = `Please delete state '${workflowStateToDelete.name}' and make state '${firstRemainingState.name}' the workflow's start state.`;
            } else if (workflowStateToDelete.isStartState && (existingStartStates.length > 1)) {
                let firstRemainingState: WorkflowState = remainingStates[0];

                if (bWorkflowHasBeenSaved && (workflowStateToDelete.id > 0)) {
                    stateToMoveObjectsInDeletedState = firstRemainingState;

                    confirmationMessage = moveAnyObjectsText + `to state '${firstRemainingState.name}' and save the workflow.`;
                } else
                    confirmationMessage = `Please delete state '${workflowStateToDelete.name}?`;
            } else if (workflowStateToDelete.isEndState && (existingEndStates.length == 1)) {
                let lastRemainingState: WorkflowState = remainingStates[remainingStates.length - 1];

                newEndState = lastRemainingState;

                if (bWorkflowHasBeenSaved && (workflowStateToDelete.id > 0)) {
                    stateToMoveObjectsInDeletedState = lastRemainingState;

                    confirmationMessage = moveAnyObjectsText + `to state '${lastRemainingState.name}' and save the workflow.`;
                } else
                    confirmationMessage = `Please delete state '${workflowStateToDelete.name}' and make state '${lastRemainingState.name}' the workflow's end state.`;
            } else if (workflowStateToDelete.isEndState && (existingEndStates.length > 1)) {
                let lastRemainingState: WorkflowState = remainingStates[remainingStates.length - 1];

                if (bWorkflowHasBeenSaved && (workflowStateToDelete.id > 0)) {
                    stateToMoveObjectsInDeletedState = lastRemainingState;

                    confirmationMessage = moveAnyObjectsText + `to state '${lastRemainingState.name}' and save the workflow.`;
                } else
                    confirmationMessage = `Please delete state '${workflowStateToDelete.name}'?`;
            } else {
                let startState: WorkflowState = remainingStates.find(ws => ws.isStartState);
                let endState: WorkflowState = remainingStates.find(ws => ws.isEndState);

                if ((startState != null) && (endState != null)) {
                    if (bWorkflowHasBeenSaved && (workflowStateToDelete.id > 0)) {
                        stateToMoveObjectsInDeletedState = startState;

                        confirmationMessage = moveAnyObjectsText + `to start state '${startState.name}' and save the workflow.`;
                    } else
                        confirmationMessage = `Please delete state '${workflowStateToDelete.name}'?`;
                } else if (startState == null) {
                    // Confirm making the first state the start state.
                    startState = remainingStates[0];
                    newStartState = startState;

                    if (workflowStateToDelete.id > 0) {
                        stateToMoveObjectsInDeletedState = startState;

                        confirmationMessage = moveAnyObjectsText + `to state '${startState.name}', make the latter the start state, and save the workflow.`;
                    } else {
                        confirmationMessage = `Please delete state '${workflowStateToDelete.name}' and make state '${startState.name}' the start state`;
                    }
                } else {
                    // Confirm making the last state the end state.
                    endState = remainingStates[remainingStates.length - 1];
                    newEndState = endState;

                    if (workflowStateToDelete.id > 0) {
                        stateToMoveObjectsInDeletedState = startState;

                        confirmationMessage = moveAnyObjectsText + `to state '${endState.name}', make the latter the end state, and save the workflow.`;
                    } else {
                        confirmationMessage = `Please delete state '${workflowStateToDelete.name}' and make state '${endState.name}' the end state.`;
                    }
                }
            }

            if (x.fI_Names) {
                if (x.fI_Names.length > 0) {
                    showAlertDialog = true;
                }
            }

            if (x.folder_Names) {
                if (x.folder_Names.length > 0) {
                    showAlertDialog = true;
                }
            }

            if (showAlertDialog) {
                let AdditionalInformation: string = `For a more in-depth guide on solutions for this issue, please visit <a target="_blank" href="https://community.max.gov/x/I_rSig">Deleting Workflow State Associated with Form Instances or Folders</a> `
                let alertTitle: string = `Cannot Delete Workflow State '${workflowStateToDelete.name}'`;
                let alertModel: AlertDialogModel;

                if (confirmationMessage != null)
                    alertModel = new AlertDialogModel(alertTitle, confirmationMessage, null, AdditionalInformation);
                else
                    alertModel = new AlertDialogModel(alertTitle, alertMessage, null, null);

                const dialogueRef = this.dialog.open(AlertDialogComponent, {
                    data: alertModel
                });
                dialogueRef.afterClosed().subscribe(answer => {
                    // Nothing to do.
                });
            }
            else {
                let confirmationTitle: string = `Delete Workflow State '${workflowStateToDelete.name}'`;
                let warningTest: string = `Are you sure you want to delete the Workflow State '${workflowStateToDelete.name}'?`;
                let confirmationData: ConfirmationDialogModel = new ConfirmationDialogModel(confirmationTitle, warningTest);

                const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: confirmationData });
                dialogRef.afterClosed().subscribe(yes => {
                    if (yes) {
                        let bSaveRequired: boolean = (stateToMoveObjectsInDeletedState != null);
                        this.doOnWorkflowStateDroppedOnTrash(workflowStateToDelete, newStartState, newEndState, bSaveRequired, stateToMoveObjectsInDeletedState);
                    }
                });
            }

        });

    }

    private doOnWorkflowStateDroppedOnTrash(workflowStateToDelete: WorkflowState,
        newStartState: WorkflowState,
        newEndState: WorkflowState,
        bSaveRequired: boolean,
        stateToMoveObjectTo: WorkflowState): void {

        // Remove any workflow transitions that start or end with the specified state.
        if (this.workflow.transitions) {
            this.workflow.transitions =
                this.workflow.transitions
                    .filter(wt => (wt.startStateId != workflowStateToDelete.id) && (wt.endStateId != workflowStateToDelete.id));
        } else {
            this.workflow.transitions = [];
        }

        // Remove the workflow state.
        this.workflow.states =
            this.workflow.states.filter(ws => ws.clientId != workflowStateToDelete.clientId);

        // Remove the workflow from the workflow state hashes.
        if (this.hshWorkflowStatesById.hasOwnProperty(workflowStateToDelete.id)) {
            delete this.hshWorkflowStatesById[workflowStateToDelete.id];
        }
        if (this.hshWorkflowStatesByClientId.hasOwnProperty(workflowStateToDelete.clientId)) {
            delete this.hshWorkflowStatesByClientId[workflowStateToDelete.clientId];
        }

        // Do we have a new start or end state?
        if (newStartState != null) {
            let existingState = this.workflow.states.find(ws => ws.clientId == newStartState.clientId);
            if (existingState != null)
                existingState.isStartState = true;
        }
        if (newEndState != null) {
            let existingState = this.workflow.states.find(ws => ws.clientId == newEndState.clientId);
            if (existingState != null)
                existingState.isEndState = true;
        }

        // Rebuild the forward and backward transitions arrays.
        this.loadForwardAndBackwardTransitions();

        // Do we need to save the workflow?
        if (bSaveRequired) {
            if (stateToMoveObjectTo != null)
                this.saveWorkflow(true, workflowStateToDelete, stateToMoveObjectTo);
            else
                this.saveWorkflow();
        }
        // Done.
        return;
    }

    private onWorkflowTransitionDroppedOnTrash(eventData: any): void {
        // Get the dragged workflow transition.
        let workflowTransitionToDelete: WorkflowTransition = eventData['dragData'];

        // Remove the specified workflow transition.
        if (this.workflow.transitions) {
            let iNumExistingTransitions: number = this.workflow.transitions.length;

            this.workflow.transitions =
                this.workflow.transitions
                    .filter(wt =>
                        !((wt.transientStartStateClientId == workflowTransitionToDelete.transientStartStateClientId) &&
                            (wt.transientEndStateClientId == workflowTransitionToDelete.transientEndStateClientId)));

            if (iNumExistingTransitions != this.workflow.transitions.length) {
                // Rebuild the forward and backward transitions arrays.
                this.loadForwardAndBackwardTransitions();
            }
        }

        return;
    }
    // End methods related to drag and drop.

    // Handle control events.
    public savePropertiesClicked(): void {
        this.multiPurposeDrawer.close();

        // Delete the drawer
        // operator cancelled operator.
        this.drawerCancelledOperator = null;
        this.flagDirty();
        return;
    }

    public cancelPropertiesClicked(): void {
        this.multiPurposeDrawer.close();

        // Use and then delete the drawer cancelled operator.
        //
        // Note: the drawer cancelled operator will revert
        //       the object being edited to its prior state.
        this.cancelDrawerAction();
        this.flagClean();

        return;
    }

    // Define the workflowSettingsClicked() and saveWorkflow() methods.
    public workflowSettingsClicked(): void {
        // Edit the workflow's name.
        this.hasIdAndName = this.workflow;
        this.drawerCancelledOperator = new WorkflowObjectEditCancelled(this.workflow);
        this.idAndNameObjectType = WorkflowEnum.WORKFLOW;

        // Open the drawer.
        this.multiPurposeDrawer.open();

        return;
    }

    public saveWorkflow(needToMoveObjectsToDifferentWorkflowState: boolean = false,
        stateToMoveObjectsFromState: WorkflowState = null,
        stateToMoveObjectsToState: WorkflowState = null): void {

        // Do we need to move any form instances and/or folders from one state to another?
        this.workflow.needToMoveObjectsToDifferentWorkflowState = needToMoveObjectsToDifferentWorkflowState;
        this.workflow.stateToMoveObjectsFromState = stateToMoveObjectsFromState;
        this.workflow.stateToMoveObjectsToState = stateToMoveObjectsToState;

        // Is the entered workflow name unique for the site?
        this.dataCollectionService.getWorkflowsBySite(this.workflow.dataCollectionId).then(workflows => {
            let existingWorkflow: Workflow = workflows.find(wf => (wf.name == this.workflow.name) && (wf.id != this.workflow.id));

            if (existingWorkflow == null) {
                this.doSaveWorkflow();
            } else {
                let message: string = WorkflowErrorMessages.duplicateWorkflowInSiteMessage(this.workflow.name, this.workflow.dataCollectionId);
                this.dialog.open(AlertDialogComponent, {
                    maxWidth: "600px",
                    data: new AlertDialogModel("Cannot Save Workflow", message)
                });
            }
        });

        return;
    }

    private doSaveWorkflow(): void {
        // Ask the service to save the workflow.
        this.workflowService.save(this.workflow).then((savedWorkflow: Workflow) => {
            // Save/process the workflow.
            this.workflow = savedWorkflow;

            //VNEXT-235: KLW - Form the URL that will direct to the newly created workflow to be routed on a page refresh
            let savedWorkflowURL = `/site/${this.site.id}/design-workflow/${this.workflow.id}`;
            this.appComponent.setWorkflowURLRefresh(savedWorkflowURL);

            this.workflowLoaded();

            // Update progress.
            // Schedule a 100% complete progress update.
            setTimeout(() => {
                this.progress100Percent();
            }, 500);

            this.flagClean();
        }).catch((reason: any) => {
            console.log('error');
            console.log(reason);
        });
    }

    // Implement private helper methods.
    private flagDirty() {
        this.isDirty = true;
    }

    private flagClean() {
        this.isDirty = false;
    }

    private workflowLoaded(bInitialComponentLoad: boolean = false): void {
        // Save a reference to the workflow.

        // If this is a new workflow, set
        // it's data collection/site Id.
        if (this.workflow.id == 0) {
            if (!this.workflow.hasGeneratedName) {
                this.workflow.dataCollectionId = this.site.id;

                let iNumberPartOfNewWorkflowName: number = 1;
                if ((this.site.workflows) && (this.site.workflows.length > 0)) {
                    iNumberPartOfNewWorkflowName = this.site.workflows.length + 1;
                }
                this.workflow.name = `Workflow ${iNumberPartOfNewWorkflowName}`;

                this.workflow.hasGeneratedName = true;
            }

            if (bInitialComponentLoad) {
                // For new workflows, start in 'Design'/build mode.
                this.viewingMode = WorkflowViewingMode.BuilderMode;
            }
        } else {

            if (bInitialComponentLoad) {
                // For existing workflows, start in permissions mode.
                //VNEXT-348: KLW - Force to show in building mode
                this.viewingMode = WorkflowViewingMode.BuilderMode;
            }
        }

        // Sort states by their order.
        this.sortWorkflowStates();

        // Index workflow states by Id.
        this.hshWorkflowStatesById = {};
        this.hshWorkflowStatesByClientId = {};

        // Assign state client Id values.
        if (this.workflow.states && (this.workflow.states.length > 0)) {

            for (let iState: number = 0; iState < this.workflow.states.length; iState++) {
                let state: WorkflowState = this.workflow.states[iState];

                this.hshWorkflowStatesById[state.id] = state;

                state.clientId = this.getNextUniqueWorkflowStateClientId();
                this.hshWorkflowStatesByClientId[state.clientId] = state;

                if ((!state.stateOrder) || (state.stateOrder == 0)) {
                    state.stateOrder = this.getNextWorkflowStateOrder();
                }
            }

            // Assign transition state client Id values.
            if ((this.workflow.transitions) && (this.workflow.transitions.length > 0)) {
                for (let iTrans: number = 0; iTrans < this.workflow.transitions.length; iTrans++) {
                    let transition: WorkflowTransition = this.workflow.transitions[iTrans];

                    let startState: WorkflowState = this.hshWorkflowStatesById[transition.startStateId];
                    let endState: WorkflowState = this.hshWorkflowStatesById[transition.endStateId];

                    if ((!startState) || (!endState)) {
                        this.throwError('workflowLoaded():  encountered a transition workflow state with an invalid Id value.');
                    }

                    transition.transientStartStateClientId = startState.clientId;
                    transition.transientEndStateClientId = endState.clientId;

                    transition.clientId = this.getNextUniqueWorkflowTransitionClientId();
                } // for
            } // if
        } else {
            // Create an initial workflow state.
            this.createInitialWorkflowState();
        }

        // Sort and organization transitions by direction.
        this.loadForwardAndBackwardTransitions();

        return;
    }

    private createInitialWorkflowState(): void {
        if (!this.workflow.states) {
            this.workflow.states = [];
        }

        let initialWorkflowState: WorkflowState = new WorkflowState();
        initialWorkflowState.name = 'Setup';
        initialWorkflowState.isStartState = true;
        initialWorkflowState.clientId = this.getNextUniqueWorkflowStateClientId();
        initialWorkflowState.stateOrder = this.getNextWorkflowStateOrder();
        initialWorkflowState.notify = false;
        initialWorkflowState.showSendNotificationsButton = false;

        this.workflow.states.push(initialWorkflowState);

        //VNEXT-348: KLW - Be sure to show all roles
        this.setMissingStatePermissions(initialWorkflowState, this.SiteRoles);

        this.hshWorkflowStatesByClientId[initialWorkflowState.clientId] = initialWorkflowState;

        return;
    }

    private getNextUniqueWorkflowStateClientId(): number {
        let iUniqueId: number = 0;

        if ((!this.workflow.states) || (this.workflow.states.length == 0)) {
            iUniqueId = 1;
        } else {
            for (let iState: number = 0; iState < this.workflow.states.length; iState++) {
                let workflowState: WorkflowState = this.workflow.states[iState];

                if (workflowState.clientId >= iUniqueId) {
                    iUniqueId = workflowState.clientId + 1;
                }
            }
        }

        return (iUniqueId);
    }

    private getNextUniqueWorkflowTransitionClientId(): number {
        let iUniqueId: number = 0;

        if ((!this.workflow.transitions) || (this.workflow.transitions.length == 0)) {
            iUniqueId = 1;
        } else {
            for (let iTrans: number = 0; iTrans < this.workflow.transitions.length; iTrans++) {
                let transition: WorkflowTransition = this.workflow.transitions[iTrans];

                if (transition.clientId >= iUniqueId) {
                    iUniqueId = transition.clientId + 1;
                }
            }
        }

        return (iUniqueId);
    }

    private getNextWorkflowStateOrder(): number {
        let iNextOrder: number = 1; // Use one-based indexing.

        if (this.workflow.states && (this.workflow.states.length > 0)) {
            for (let iState: number = 0; iState < this.workflow.states.length; iState++) {
                let workflowState: WorkflowState = this.workflow.states[iState];

                if (workflowState.stateOrder <= 0) {
                    this.throwError('getNextWorkflowStateOrder():  encountered a workflow state with an invalid stateOrder value.');
                } // if

                if (workflowState.stateOrder >= iNextOrder) {
                    iNextOrder = workflowState.stateOrder + 1;
                }
            } // for
        } // if

        return (iNextOrder);
    }

    private loadForwardAndBackwardTransitions(): void {
        this.forwardWorkflowTransitions = [];
        this.backwardWorkflowTransitions = [];

        if (this.workflow.transitions && (this.workflow.transitions.length > 0)) {
            // First sort the workflow's transitions.
            this.sortWorkflowTransitions();

            // Now pull out the forward and backward transitions.
            for (let iTransition: number = 0; iTransition < this.workflow.transitions.length; iTransition++) {
                let transition: WorkflowTransition = this.workflow.transitions[iTransition];

                let startState: WorkflowState = this.getTransitionStartState(transition);
                let endState: WorkflowState = this.getTransitionEndState(transition);

                if (startState && endState) {
                    if (startState.stateOrder < endState.stateOrder) {
                        this.forwardWorkflowTransitions.push(transition);
                    } else {
                        this.backwardWorkflowTransitions.push(transition);
                    }
                } else {
                    if (!startState) {
                        console.log(`WorkflowComponent.workflowLoaded():  cannot find workflow state ${transition.startStateId}.`);
                    }
                    if (!endState) {
                        console.log(`WorkflowComponent.workflowLoaded():  cannot find workflow state ${transition.endStateId}.`);
                    }
                } // if-else
            } // for

            // Reset states before and after each transition
            // so that they get calculated anew.
            for (let iTransition: number = 0; iTransition < this.workflow.transitions.length; iTransition++) {
                let transition: WorkflowTransition = this.workflow.transitions[iTransition];

                transition.transientStatesBeforeTransition = null;
                transition.transientStatesAfterTransition = null;
            }
        } // if

        return;
    }

    private sortWorkflowStates(): void {
        if (this.workflow.states && (this.workflow.states.length > 0)) {
            this.workflow.states.sort(
                function (state1: WorkflowState, state2: WorkflowState) {
                    let iCompare = (state1.stateOrder < state2.stateOrder ? -1 : 1);

                    return (iCompare);
                });
        }

        return;
    }

    private sortWorkflowTransitions(): void {
        if (this.workflow.transitions && (this.workflow.transitions.length > 0)) {
            // Sort the transitions by forward first, then backward,
            // then by start state order and end state order.
            let hshWorkflowStatesByClientId: IWorkflowStateIdToWorkflowState = this.hshWorkflowStatesByClientId;

            this.workflow.transitions.sort(
                function (transition1: WorkflowTransition, transition2: WorkflowTransition) {
                    let iCompare: number = -1; // Default

                    let t1StartState: WorkflowState = WorkflowComponent.getTransitionStartState(transition1, hshWorkflowStatesByClientId);
                    let t1EndState: WorkflowState = WorkflowComponent.getTransitionEndState(transition1, hshWorkflowStatesByClientId);
                    let bT1IsForwardTransition: boolean = true;
                    if (t1StartState && t1EndState) {
                        bT1IsForwardTransition = t1StartState.stateOrder < t1EndState.stateOrder;
                    }

                    let t2StartState: WorkflowState = WorkflowComponent.getTransitionStartState(transition2, hshWorkflowStatesByClientId);
                    let t2EndState: WorkflowState = WorkflowComponent.getTransitionEndState(transition2, hshWorkflowStatesByClientId);
                    let bT2IsForwardTransition: boolean = true;
                    if (t2StartState && t2EndState) {
                        bT2IsForwardTransition = t2StartState.stateOrder < t2EndState.stateOrder;
                    }

                    if (bT1IsForwardTransition && (!bT2IsForwardTransition)) {
                        iCompare = -1;
                    } else if ((!bT1IsForwardTransition) && bT2IsForwardTransition) {
                        iCompare = 1;
                    } else if (bT1IsForwardTransition) {
                        if (t1StartState.stateOrder != t2StartState.stateOrder) {
                            iCompare = (t1StartState.stateOrder < t2StartState.stateOrder ? -1 : 1);
                        } else {
                            iCompare = (t1EndState.stateOrder < t2EndState.stateOrder ? -1 : 1);
                        }
                    } else {
                        if (t1StartState.stateOrder != t2StartState.stateOrder) {
                            iCompare = (t1StartState.stateOrder > t2StartState.stateOrder ? -1 : 1);
                        } else {
                            iCompare = (t1EndState.stateOrder > t2EndState.stateOrder ? -1 : 1);
                        }
                    }

                    return (iCompare);
                });

            // Assign client order values.
            for (let iTrans: number = 0; iTrans < this.workflow.transitions.length; iTrans++) {
                let workflowTransition: WorkflowTransition = this.workflow.transitions[iTrans];

                workflowTransition.transientClientOrder = iTrans + 1;
            }
        }

        // Done.
        return;
    }

    private sortWorkflowTransitionsForNewConfigureRolesDialogue(): void {
        if (this.workflow.transitions && (this.workflow.transitions.length > 0)) {
            // Sort the transitions by forward first, then backward,
            // then by start state order and end state order.
            let hshWorkflowStatesByClientId: IWorkflowStateIdToWorkflowState = this.hshWorkflowStatesByClientId;

            this.workflow.transitions.sort(
                function (transition1: WorkflowTransition, transition2: WorkflowTransition) {
                    let iCompare: number = -1; // Default

                    let t1StartState: WorkflowState = WorkflowComponent.getTransitionStartState(transition1, hshWorkflowStatesByClientId);
                    let t1EndState: WorkflowState = WorkflowComponent.getTransitionEndState(transition1, hshWorkflowStatesByClientId);
                    let bT1IsForwardTransition: boolean = true;
                    if (t1StartState && t1EndState) {
                        bT1IsForwardTransition = t1StartState.stateOrder < t1EndState.stateOrder;
                    }

                    let t2StartState: WorkflowState = WorkflowComponent.getTransitionStartState(transition2, hshWorkflowStatesByClientId);
                    let t2EndState: WorkflowState = WorkflowComponent.getTransitionEndState(transition2, hshWorkflowStatesByClientId);
                    let bT2IsForwardTransition: boolean = true;
                    if (t2StartState && t2EndState) {
                        bT2IsForwardTransition = t2StartState.stateOrder < t2EndState.stateOrder;
                    }

                    if (bT1IsForwardTransition && (!bT2IsForwardTransition)) {
                        iCompare = 1;
                    } else if ((!bT1IsForwardTransition) && bT2IsForwardTransition) {
                        iCompare = -1;
                    } else if (bT1IsForwardTransition) {
                        if (t1StartState.stateOrder != t2StartState.stateOrder) {
                            iCompare = (t1StartState.stateOrder < t2StartState.stateOrder ? -1 : 1);
                        } else {
                            iCompare = (t1EndState.stateOrder < t2EndState.stateOrder ? -1 : 1);
                        }
                    } else {
                        if (t1StartState.stateOrder != t2StartState.stateOrder) {
                            iCompare = (t1StartState.stateOrder > t2StartState.stateOrder ? -1 : 1);
                        } else {
                            iCompare = (t1EndState.stateOrder > t2EndState.stateOrder ? -1 : 1);
                        }
                    }

                    return (iCompare);
                });

            // Assign client order values.
            for (let iTrans: number = 0; iTrans < this.workflow.transitions.length; iTrans++) {
                let workflowTransition: WorkflowTransition = this.workflow.transitions[iTrans];

                workflowTransition.transientClientOrder = iTrans + 1;
            }
        }

        // Done.
        return;
    }

    private static findUniqueWorkflowStateName(workflow: Workflow, proposedStateNumber: number): string {
        let bUniqueNameFound: boolean = ((!workflow.states) || (workflow.states.length == 0));

        let strTestName: string = `State ${proposedStateNumber}`;

        while (!bUniqueNameFound) {
            bUniqueNameFound = true;

            for (let iState: number = 0; iState < workflow.states.length; iState++) {
                let workflowState: WorkflowState = workflow.states[iState];

                if (strTestName == workflowState.name) {
                    bUniqueNameFound = false;

                    break;
                } // if
            } // for

            if (!bUniqueNameFound) {
                proposedStateNumber++;

                strTestName = `State ${proposedStateNumber}`;
            }
        } // while

        return (strTestName);
    }

    private static findWorkflowStateIndex(workflow: Workflow, workflowState: WorkflowState): number {
        let iStateIndex: number = -1;

        for (let iState: number = 0; iState < workflow.states.length; iState++) {
            let seekWorkflowState: WorkflowState = workflow.states[iState];

            if (seekWorkflowState.name == workflowState.name) {
                iStateIndex = iState;

                break;
            } // if
        } // for

        return (iStateIndex);
    }

    private onExistingWorkflowStateDropped(eventData: any,
        droppedOnWorkflowState: WorkflowState,
        bBeforeWorkflowState: boolean): void {
        // Get the index of the dropped on workflow state.
        let iDroppedOnStateIndex: number =
            WorkflowComponent.findWorkflowStateIndex(this.workflow, droppedOnWorkflowState);

        let bDraggedStateMoved: boolean =
            WorkflowComponent.droppedExistingWorkflowStateMoved(iDroppedOnStateIndex,
                bBeforeWorkflowState,
                this.iExistingWorkflowStateBeingDraggedIndex);

        if (bDraggedStateMoved) {
            let arrExistingWorkflowStateDropped: WorkflowState[] =
                this.workflow.states.splice(this.iExistingWorkflowStateBeingDraggedIndex, 1);

            if (arrExistingWorkflowStateDropped && (arrExistingWorkflowStateDropped.length == 1)) {
                let existingWorkflowStateDropped: WorkflowState = arrExistingWorkflowStateDropped[0];

                let iUpdatedDroppedOnStateIndex =
                    (this.iExistingWorkflowStateBeingDraggedIndex < iDroppedOnStateIndex ?
                        iDroppedOnStateIndex - 1 : iDroppedOnStateIndex);

                // Move the dragged workflow state.
                if (bBeforeWorkflowState) {
                    this.workflow.states.splice(iUpdatedDroppedOnStateIndex, 0, existingWorkflowStateDropped);
                } else {
                    if (iUpdatedDroppedOnStateIndex < this.workflow.states.length - 1) {
                        this.workflow.states.splice(iUpdatedDroppedOnStateIndex + 1, 0, existingWorkflowStateDropped);
                    } else {
                        this.workflow.states.push(existingWorkflowStateDropped);
                    }
                }

                // Reset workflow state orders.
                for (let iState: number = 0; iState < this.workflow.states.length; iState++) {
                    let workflowState: WorkflowState = this.workflow.states[iState];

                    workflowState.stateOrder = iState + 1;
                }

                // Reload transitions.
                this.loadForwardAndBackwardTransitions();
            }

            this.flagDirty();
        }

        return;
    }

    private static droppedExistingWorkflowStateMoved(iExistingStateIndex: number,
        bBeforeWorkflowState: boolean,
        iExistingWorkflowStateBeingDraggedIndex: number): boolean {
        let bAfterWorkflowState: boolean = (!bBeforeWorkflowState);

        let bWorkflowStateMoved: boolean = false;

        if (iExistingWorkflowStateBeingDraggedIndex == iExistingStateIndex) {
            // This state did not move.
        } else if ((iExistingWorkflowStateBeingDraggedIndex == iExistingStateIndex + 1) && bAfterWorkflowState) {
            // This state did not move.
        } else if ((iExistingStateIndex == iExistingWorkflowStateBeingDraggedIndex + 1) && bBeforeWorkflowState) {
            // This state did not move.
        } else {
            bWorkflowStateMoved = true;
        }

        return (bWorkflowStateMoved);
    }

    //VNEXT-348: KLW - Be sure to show all roles
    //VNEXT-392: KLW - Put in optinal parameter to add default permissions
    private setMissingStatePermissions(workflowState: WorkflowState, roles: Role[], setDefaultPermissions: boolean = true, setSuggestedPermissions: boolean = true): WorkflowState {
        roles.forEach(role => {
            if (workflowState.permissions.filter(function (e) { return e.roleName == role.name; }).length == 0) {

                let toAdd: WorkflowStatePermission = new WorkflowStatePermission();

                toAdd.roleId = role.id;
                toAdd.roleName = role.name;

                if (setDefaultPermissions)
                    toAdd = this.setDefaultPermissionsForRole(toAdd);

                //VNEXT-392: KLW - Set the suggested permissions
                if (setSuggestedPermissions)
                    toAdd = this.setSuggestedPermissionsForRole(toAdd);

                workflowState.addPermission(toAdd);

                //VNEXT-392: KLW - Set to be dirty to force user to save
                this.flagDirty();
            }
        });

        return workflowState;
    }

    //VNEXT-348: KLW - Set the current default permissions
    private setDefaultPermissionsForRole(wsp: WorkflowStatePermission): WorkflowStatePermission {

        switch (wsp.roleName) {

            case "Site Administrator":
            case "Builder":
                wsp.canEdit = true;
                wsp.canView = true;
                wsp.canGrant = true;
                wsp.canDelete = true;
                wsp.canBuildForms = true;
                wsp.canBuildWorkflows = true;
                wsp.canBuildFolders = true;
                wsp.canBuildFormInstances = true;
                wsp.canBuildRoles = true;
                break;

            case "Contributor":
                wsp.canEdit = true;
                break;

            case "Reviewer":
                wsp.canView = true;
                break;

            default:
                break;
        }

        return wsp;

    }

    //VNEXT-392: KLW - Method to set the suggested permissions
    private setSuggestedPermissionsForRole(wsp: WorkflowStatePermission): WorkflowStatePermission {

        switch (wsp.roleName) {

            case "Site Administrator":
            case "Builder":
                wsp.suggestCanEdit = true;
                wsp.suggestCanView = true;
                wsp.suggestCanGrant = true;
                wsp.suggestCanDelete = true;
                wsp.suggestCanBuildForms = true;
                wsp.suggestCanBuildWorkflows = true;
                wsp.suggestCanBuildFolders = true;
                wsp.suggestCanBuildFormInstances = true;
                wsp.suggestCanBuildRoles = true;
                break;

            case "Contributor":
                wsp.suggestCanEdit = true;
                break;

            case "Reviewer":
                wsp.suggestCanView = true;
                break;

            default:
                break;
        }

        return wsp;

    }

    private onNewWorkflowStateDropped(eventData: any, workflowState: WorkflowState, bBeforeWorkflowState: boolean): void {
        // Create a new state name (the user will change it).
        let iStateNum: number = (this.workflow.states ? this.workflow.states.length + 1 : 1);
        let newStateName: string =
            WorkflowComponent.findUniqueWorkflowStateName(this.workflow, iStateNum);

        // Create a new workflow state with the initial name.
        let newWorkflowState: WorkflowState = new WorkflowState();
        newWorkflowState.name = newStateName;
        newWorkflowState.clientId = this.getNextUniqueWorkflowStateClientId();
        newWorkflowState.stateOrder = this.getNextWorkflowStateOrder();

        //VNEXT-348: KLW - Be sure to show all actions
        this.setMissingStatePermissions(newWorkflowState, this.SiteRoles);

        // Insert the new workflow state after the existing state.
        let iExistingStateIndex: number =
            WorkflowComponent.findWorkflowStateIndex(this.workflow, workflowState);

        if (bBeforeWorkflowState) {
            this.workflow.states.splice(iExistingStateIndex, 0, newWorkflowState);
        } else if (iExistingStateIndex < this.workflow.states.length - 1) {
            this.workflow.states.splice(iExistingStateIndex + 1, 0, newWorkflowState);
        } else {
            this.workflow.states.push(newWorkflowState);
        }

        // Reset workflow state orders.
        for (let iState: number = 0; iState < this.workflow.states.length; iState++) {
            let workflowState: WorkflowState = this.workflow.states[iState];

            workflowState.stateOrder = (iState + 1);
        }

        // Add the new workflow state to the workflow state hashes.
        this.hshWorkflowStatesByClientId[newWorkflowState.clientId] = newWorkflowState;
        this.flagDirty();

        // Open the drawer to save the user a step.
        this.workflowStateClicked(null, newWorkflowState, null);

        // Done.
        return;
    }

    // Note:  the following method is being moved to workflow-transition.component.ts.
    private getTransitionStartState(transition: WorkflowTransition): WorkflowState {
        let startState: WorkflowState =
            this.hshWorkflowStatesByClientId[transition.transientStartStateClientId];

        if (!startState) {
            this.throwError('getTransitionStartState():  cannot find a workflow state by client Id.');
        }

        return (startState);
    }

    // Note:  the following method is being moved to workflow-transition.component.ts.
    private getTransitionEndState(transition: WorkflowTransition): WorkflowState {
        let endState: WorkflowState = this.hshWorkflowStatesByClientId[transition.transientEndStateClientId];

        if (!endState) {
            this.throwError('getTransitionEndState():  cannot find a workflow state by client Id.');
        }

        return (endState);
    }

    public static getTransitionStartState(transition: WorkflowTransition,
        hshWorkflowStatesByClientId: IWorkflowStateIdToWorkflowState): WorkflowState {
        let startState: WorkflowState = hshWorkflowStatesByClientId[transition.transientStartStateClientId];

        if (!startState) {
            WorkflowComponent.throwError('getTransitionStartState():  cannot find a workflow state by client Id.');
        }

        return (startState);
    }

    public static getTransitionEndState(transition: WorkflowTransition,
        hshWorkflowStatesByClientId: IWorkflowStateIdToWorkflowState): WorkflowState {
        let endState: WorkflowState = hshWorkflowStatesByClientId[transition.transientEndStateClientId];

        if (!endState) {
            WorkflowComponent.throwError('getTransitionEndState():  cannot find a workflow state by client Id.');
        }

        return (endState);
    }

    private generateNewTransitionName(startWorkflowState: WorkflowState, endWorkflowState: WorkflowState): string {
        let strTransitionName: string = `${startWorkflowState.name} to ${endWorkflowState.name}`;

        if (this.workflow && this.workflow.transitions) {
            let iUniqueNameAttempt: number = 1;
            let bNameIsUnique: boolean = true;

            do {
                for (let iTrans: number = 0; iTrans < this.workflow.transitions.length; iTrans++) {
                    let transition: WorkflowTransition = this.workflow.transitions[iTrans];

                    if (transition.name == strTransitionName) {
                        // Name is not unique.
                        bNameIsUnique = false;

                        strTransitionName =
                            `${startWorkflowState.name} to ${endWorkflowState.name} ${iUniqueNameAttempt}`;
                        iUniqueNameAttempt++;

                        break;
                    }
                }
            } while (!bNameIsUnique);
        }

        return (strTransitionName);
    }

    public static throwError(errorMsg: string): void {
        throw (errorMsg);
    }

    private progress100Percent(): void {
        setTimeout(() => {
            this.hideProgress();
        }, 3000);

        return;
    }

    private hideProgress(): void {
        this.progressIndicatorService.hideProgress();

        return;
    }

    private openMultipurposeDrawer(): void {
        this.multiPurposeDrawer.open();

        return;
    }

    private cancelDrawerAction(): void {
        if (this.drawerCancelledOperator != null) {
            this.drawerCancelledOperator.drawerCancelled();
            this.drawerCancelledOperator = null;
        }

        return;
    }
}

